export const KEY_DELIMITER = '#'

export const LEVELS = ['state', 'location', 'unitGroup', 'unitType']
export const LEVEL_LABELS = { state: 'State', location: 'Location', unitGroup: 'Unit Group', unitType: 'Unit Type' }

export const AMENITY_SUFFIXES = ['UP', 'GR', 'DR', 'UC', 'MO', 'OP']
const IGNORED_UNIT_GROUPS = ['WIN', 'GUN', 'DB']

import { pctChange, fmtPrice, fmtPct, fmtOcc } from '@/utils/formatUtils'
import { recordValidation, currentPage } from '@/utils/validationHook'
import {
  buildNumberBoundFromOperator,
  isAdvancedFilterEnabled,
  matchesBooleanFilter,
  matchesNumberBound,
  matchesStringFilter,
} from '@/utils/rentalFilterUtils'
export { pctChange, fmtPrice, fmtPct, fmtOcc }

const PRICING_RECORD_FIELDS = ['streetRent', 'modelRent', 'ruleRent', 'finalRent', 'changePct', 'occupancy', 'marketOccupancy']

function recordPricingNode(component, node) {
  const key = node.path || node.id
  if (!key) return
  // buildTree is shared across pages; tag records with the active route so
  // captures from Bubble Plots / Pricing Rules don't collide with Pricing Actions.
  const page = currentPage('pricing-actions')
  for (const field of PRICING_RECORD_FIELDS) {
    if (node[field] != null) recordValidation(page, component, `${key}:${field}`, node[field])
  }
}

function parsePath(path) {
  return path.split(KEY_DELIMITER)
}

function getCompany(parts) { return parts[0] }
function getStateName(parts) { return parts[1] }
function getStateKey(parts) { return parts.slice(0, 2).join(KEY_DELIMITER) }
function getLocationKey(parts) { return parts.slice(0, 3).join(KEY_DELIMITER) }
function getUnitGroupKey(parts) { return parts.length >= 5 ? parts.slice(0, 4).join(KEY_DELIMITER) : null }

function emptyAgg() {
  return {
    streetRent: 0, streetRate: 0,
    modelRent: 0, modelRate: 0,
    ruleRent: 0, ruleRate: 0,
    overrideRent: 0, overrideRate: 0,
    finalRent: 0, finalRate: 0,
    changePct: 0, changePctRate: 0,
    moveInRent: 0, moveInRate: 0,
    moveOutRent: 0, moveOutRate: 0,
    occupiedRent: 0, occupiedRate: 0,
    competitorRent: 0, competitorRate: 0,
    occupancy: 0, marketOccupancy: 0, occupancySqm: 0,
    occDelta7d: null, occDelta28d: null, occDelta1y: null,
    occDeltaSqm7d: null, occDeltaSqm28d: null, occDeltaSqm1y: null,
    occUnits7d: 0, occUnits28d: 0, occUnits1y: 0,
    rentUnits7d: 0, rentUnits28d: 0, rentUnits1y: 0,
    occSqm7d: 0, occSqm28d: 0, occSqm1y: 0,
    rentSqm7d: 0, rentSqm28d: 0, rentSqm1y: 0,
    monthlyRevenue: 0,
    rentableUnits: 0, occupiedUnits: 0,
    vacantUnits: 0, reservedUnits: 0,
    totalUnits: 0, repairUnits: 0, companyUnits: 0,
    moveInUnits: 0, moveOutUnits: 0,
    rentedUnits: 0, rented28dUnits: 0,
    sqmRentable: 0, sqmRented: 0, sqmOccupied: 0, sqmTotal: 0, sqmVacant: 0,
    sqmReserved: 0, sqmCompany: 0, sqmRepair: 0,
  }
}

// PriceChangeSource enum (proto): CURRENT=0, RECOMMENDED=1, RULE_BASED=2, MANUAL=3
const PRICE_SOURCE_TO_CHOICE = { 0: 'current', 1: 'auto', 2: 'rule', 3: 'override' }
const CHOICE_TO_PRICE_SOURCE = { current: 0, auto: 1, rule: 2, override: 3, street: 0, model: 1 }
const PRICE_STATUS_TO_DECISION = {
  0: 'undecided',
  1: 'approved',
  2: 'rejected',
  3: 'sentToStorman',
  4: 'archived',
  5: 'sentToStormanFailed',
  6: 'notWhitelisted',
}

/** Map gRPC PriceChangeStatus ints onto UI decision labels. */
export function priceChangeStatusToDecision(status) {
  return PRICE_STATUS_TO_DECISION[status] ?? 'undecided'
}

/** Map UI choice labels (or numeric enums) onto PriceChangeSource int values. */
export function choiceToPriceChangeSource(choice) {
  if (typeof choice === 'number' && Number.isFinite(choice)) return choice
  return CHOICE_TO_PRICE_SOURCE[choice] ?? CHOICE_TO_PRICE_SOURCE.auto
}

// Resolve the displayed final price exactly like KSS-PROD
// (ApiData.newDecisionFromChanges + ApiData.updateRow): each component price
// falls back to street when missing, an undecided CURRENT choice is promoted to
// 'auto' (recommended), and the final is then selected purely from `choice`.
// NOTE: the gRPC PriceChange fields are recommendedPrice / ruleBasedPrice /
// manualPrice — not rulePrice / overridePrice.
function resolvePricing(pc, streetRent) {
  const recommended = pc && pc.recommendedPrice > 0 ? pc.recommendedPrice : streetRent
  const ruleBased = pc && pc.ruleBasedPrice > 0 ? pc.ruleBasedPrice : streetRent
  const manual = pc && pc.manualPrice > 0 ? pc.manualPrice : streetRent
  const status = pc?.status ?? 0
  // dbChoice = raw PriceChange.which from the DB.
  const dbChoice = pc ? (PRICE_SOURCE_TO_CHOICE[pc.choice] ?? 'current') : 'current'
  // choice = display/Final source: same as dbChoice, except undecided CURRENT → auto.
  const choice = dbChoice === 'current' && status === 0 ? 'auto' : dbChoice
  let finalRent
  switch (choice) {
    case 'auto': finalRent = recommended; break
    case 'rule': finalRent = ruleBased; break
    case 'override': finalRent = manual; break
    default: finalRent = streetRent
  }
  return { recommended, ruleBased, manual, choice, dbChoice, finalRent }
}

function buildLeafRow(path, parts, aggregatedData, siteNames, priceChanges, unitTypeAreas) {
  const agg = aggregatedData?.get(path) || {}
  // The getPriceChanges map is keyed by unitTypeId (the final path segment), as it
  // is in KSS-PROD where lookups use the row id. Looking it up by the full path
  // silently misses every change, so resolve the unitTypeId first.
  const unitTypeId = parts[parts.length - 1]
  const pc = priceChanges?.get(unitTypeId) ?? priceChanges?.get(path)

  const streetRent = agg.streetRent ?? 0
  const streetRate = agg.streetRate ?? 0
  const { recommended, ruleBased, choice, dbChoice, finalRent } = resolvePricing(pc, streetRent)
  const modelRent = recommended
  const ruleRent = ruleBased
  const overrideRent = pc?.manualPrice > 0 ? pc.manualPrice : 0
  // Rates track rent proportionally for a given unit area (mirrors PROD calculateRateFromRent).
  const rentToRate = (rent) => (streetRent > 0 ? (streetRate * rent) / streetRent : 0)
  const modelRate = rentToRate(modelRent)
  const ruleRate = rentToRate(ruleRent)
  const overrideRate = overrideRent > 0 ? rentToRate(overrideRent) : 0
  const finalRate = rentToRate(finalRent)
  const units = agg.units ?? {}
  const rentable = units.rentable ?? 0
  const occupied = units.occupied ?? 0
  const occupancy = rentable > 0 ? (occupied / rentable) * 100 : 0

  const unitTypeName = parts.length > 4
    ? parts.slice(4).join(' / ')
    : parts.length === 4
      ? parts[3]
      : parts[parts.length - 1]

  const utId = parts.length >= 5 ? parts[4] : parts.length === 4 ? parts[3] : null
  const size = utId && unitTypeAreas ? (unitTypeAreas.get(utId) || 0) : 0
  const amenity = getAmenitySuffix(unitTypeName) || null

  const dbDecision = priceChangeStatusToDecision(pc?.status)

  const sqm = agg.sqm ?? {}
  const vacant = units.vacant ?? 0
  const reserved = units.reserved ?? 0
  const total = units.total ?? 0
  const repair = units.repair ?? 0
  const company = units.company ?? 0
  const moveInUnits = units.moveIn ?? 0
  const moveOutUnits = units.moveOut ?? 0
  const rented = units.rented ?? 0
  const rented28d = units.rented28d ?? 0

  const sqmRentable = sqm.rentable ?? 0
  const sqmRented = sqm.rented ?? 0
  const sqmOccupied = sqm.occupied ?? 0
  const sqmTotal = sqm.total ?? 0
  const sqmVacant = sqm.vacant ?? 0
  // Reserved / company / repair m² are not always on agg.sqm; estimate from unit area.
  const sqmReserved = size > 0 ? size * reserved : (sqm.reserved ?? 0)
  const sqmCompany = size > 0 ? size * company : (sqm.company ?? 0)
  const sqmRepair = size > 0 ? size * repair : (sqm.repair ?? 0)

  const moveOutRent = agg.moveOutRent ?? 0

  const marketOccNumerator = rented + reserved
  const marketOccDenominator = rentable - repair - company
  const marketOccupancy = marketOccDenominator > 0 ? (marketOccNumerator / marketOccDenominator) * 100 : occupancy

  const occUnits7d = agg.occupancyUnits7d ?? 0
  const occUnits28d = agg.occupancyUnits28d ?? 0
  const occUnits1y = agg.occupancyUnits1y ?? 0
  const rentUnits7d = agg.rentableUnits7d ?? 0
  const rentUnits28d = agg.rentableUnits28d ?? 0
  const rentUnits1y = agg.rentableUnits1y ?? 0
  const occ7d = rentUnits7d > 0 ? (occUnits7d / rentUnits7d) * 100 : null
  const occ28d = rentUnits28d > 0 ? (occUnits28d / rentUnits28d) * 100 : null
  const occ1y = rentUnits1y > 0 ? (occUnits1y / rentUnits1y) * 100 : null
  const occDelta7d = occ7d != null ? occupancy - occ7d : null
  const occDelta28d = occ28d != null ? occupancy - occ28d : null
  const occDelta1y = occ1y != null ? occupancy - occ1y : null

  // Match Angular occupancyPercSqmNow: occupied / rentable (not rented / rentable).
  const occupancySqm = sqmRentable > 0 ? (sqmOccupied / sqmRentable) * 100 : 0
  const occSqm7d = agg.occupancySqm7d ?? 0
  const occSqm28d = agg.occupancySqm28d ?? 0
  const occSqm1y = agg.occupancySqm1y ?? 0
  const rentSqm7d = agg.rentableSqm7d ?? 0
  const rentSqm28d = agg.rentableSqm28d ?? 0
  const rentSqm1y = agg.rentableSqm1y ?? 0
  const pastOccSqm7d = rentSqm7d > 0 ? (occSqm7d / rentSqm7d) * 100 : null
  const pastOccSqm28d = rentSqm28d > 0 ? (occSqm28d / rentSqm28d) * 100 : null
  const pastOccSqm1y = rentSqm1y > 0 ? (occSqm1y / rentSqm1y) * 100 : null
  const occDeltaSqm7d = pastOccSqm7d != null ? occupancySqm - pastOccSqm7d : null
  const occDeltaSqm28d = pastOccSqm28d != null ? occupancySqm - pastOccSqm28d : null
  const occDeltaSqm1y = pastOccSqm1y != null ? occupancySqm - pastOccSqm1y : null

  const row = {
    id: path,
    path,
    name: unitTypeName,
    level: 'unitType',
    children: [],
    childCount: 0,
    expanded: false,
    detailsOpen: false,
    streetRent, streetRate,
    modelRent, modelRate,
    ruleRent, ruleRate,
    overrideRent, overrideRate,
    finalRent, finalRate,
    changePct: pctChange(streetRent, finalRent),
    changePctRate: pctChange(streetRate, finalRate),
    moveInRent: agg.moveInRent ?? 0,
    moveInRate: agg.moveInRate ?? 0,
    moveOutRent,
    moveOutRate: agg.moveOutRate ?? 0,
    occupiedRent: agg.occupiedRent ?? 0,
    occupiedRate: agg.occupiedRate ?? 0,
    competitorRent: agg.competitorRent ?? 0,
    competitorRate: agg.competitorRate ?? 0,
    competitorMinRent: agg.competitorMinRent ?? 0,
    competitorMaxRent: agg.competitorMaxRent ?? 0,
    competitorMinRate: agg.competitorMinRate ?? 0,
    competitorMaxRate: agg.competitorMaxRate ?? 0,
    occupancy,
    marketOccupancy,
    occupancySqm,
    occDelta7d, occDelta28d, occDelta1y,
    occDeltaSqm7d, occDeltaSqm28d, occDeltaSqm1y,
    occUnits7d, occUnits28d, occUnits1y,
    rentUnits7d, rentUnits28d, rentUnits1y,
    occSqm7d, occSqm28d, occSqm1y,
    rentSqm7d, rentSqm28d, rentSqm1y,
    rentableUnits: rentable,
    occupiedUnits: occupied,
    vacantUnits: vacant,
    reservedUnits: reserved,
    totalUnits: total,
    repairUnits: repair,
    companyUnits: company,
    moveInUnits,
    moveOutUnits,
    rentedUnits: rented,
    rented28dUnits: rented28d,
    sqmRentable, sqmRented, sqmOccupied, sqmTotal, sqmVacant,
    sqmReserved, sqmCompany, sqmRepair,
    size,
    amenity,
    monthlyRevenue: streetRent * occupied,
    decision: dbDecision,
    dbDecision,
    dbChoice,
    dbOverrideRent: overrideRent,
    dbOverrideExpiresOn: pc?.dateOverrideExpires || null,
    choice,
    status: pc?.status ?? 0,
    overrideExpiresOn: pc?.dateOverrideExpires || null,
    rawChange: pc || null,
    agg,
    flags: null,
  }
  recordPricingNode('leaf', row)
  return row
}

export function collectAllLeaves(node) {
  if (!node.children || node.children.length === 0) return [node]
  const leaves = []
  for (const child of node.children) {
    leaves.push(...collectAllLeaves(child))
  }
  return leaves
}

/** Walk children and amenity/size subGroups to find a node by path id. */
export function resolveNodeFromTree(tree, nodeId) {
  if (!tree?.length || !nodeId) return null
  for (const node of tree) {
    if (node.id === nodeId) return node
    if (node.children?.length) {
      const found = resolveNodeFromTree(node.children, nodeId)
      if (found) return found
    }
    if (node.subGroups?.length) {
      const found = resolveNodeFromTree(node.subGroups, nodeId)
      if (found) return found
    }
  }
  return null
}

/** Map backend / local decision values onto accept | reject | undecided | other.
 * Exported / saved Streamline statuses count as Accept so the pill stays selected
 * (production shows upload icons for approved + sentToStorman). */
export function normalizeDecision(decision) {
  if (
    decision === 'approved'
    || decision === 'accept'
    || decision === 'sentToStorman'
    || decision === 'promotion'
  ) return 'accept'
  if (decision === 'rejected' || decision === 'reject') return 'reject'
  if (!decision || decision === 'undecided') return 'undecided'
  return decision
}

export function collectDecisionLeaves(node) {
  const kids = [...(node.children || []), ...(node.subGroups || [])]
  if (kids.length === 0) return [node]
  const out = []
  for (const child of kids) out.push(...collectDecisionLeaves(child))
  return out
}

export function resolveStoredDecision(id, fallbackDecision, localDecisions = {}) {
  if (Object.prototype.hasOwnProperty.call(localDecisions, id)) {
    return normalizeDecision(localDecisions[id])
  }
  return normalizeDecision(fallbackDecision || 'undecided')
}

function tallyLeafDecisions(row, localDecisions = {}) {
  const isLeaf = (!row.children || row.children.length === 0) && !row.subGrouping
  const leaves = isLeaf ? [row] : collectDecisionLeaves(row)
  let accept = 0
  let reject = 0
  let other = 0
  for (const leaf of leaves) {
    const d = resolveStoredDecision(leaf.id, leaf.dbDecision || leaf.decision, localDecisions)
    if (d === 'accept') accept += 1
    else if (d === 'reject') reject += 1
    else other += 1
  }
  return { accept, reject, other, total: leaves.length }
}

/**
 * Effective accept/reject for a pricing row (toggle + solid pill).
 * Unanimous leaf accept/reject → that value; some (not all) accepts → `partial`
 * so folded parents show a circled Accept mark.
 */
export function getRolledUpDecision(row, localDecisions = {}) {
  const own = resolveStoredDecision(row.id, row.dbDecision || row.decision, localDecisions)
  const isLeaf = (!row.children || row.children.length === 0) && !row.subGrouping
  if (isLeaf) return own

  const { accept, reject, total } = tallyLeafDecisions(row, localDecisions)
  if (total === 0) return own
  if (accept === total) return 'accept'
  if (reject === total) return 'reject'
  if (accept > 0) return 'partial'
  return 'undecided'
}

/** Accept/reject pill colour: green/red = unsaved (ready to deploy), blue/dark red = already saved. */
export function decisionPillTone(row, ctx = {}) {
  const decision = getRolledUpDecision(row, ctx.localDecisions || {})
  if (decision === 'partial') return { decision: 'partial', tone: 'partial' }
  if (decision !== 'accept' && decision !== 'reject') return { decision: 'undecided', tone: 'idle' }
  const state = toPriceRowState(row, ctx)
  return { decision, tone: state === 'deployed' ? 'deployed' : 'ready' }
}

export function decisionPillClassName(kind, row, ctx = {}, { canPushToHb = false, sentToTenant = false } = {}) {
  const { decision, tone } = decisionPillTone(row, ctx)
  const dbDecision = row?.dbDecision || row?.decision
  const pushHb = kind === 'accept' && canPushToHb
  // Only show Sent chrome when the row is still clean Deployed (not re-dirty after Sent).
  const sent = kind === 'accept'
    && tone === 'deployed'
    && (sentToTenant || dbDecision === 'sentToStorman')
  const skipLock = kind === 'skip'
  const partial = kind === 'accept' && decision === 'partial'
  const active = skipLock
    ? decision === 'reject'
    : (decision === kind || pushHb || sent || partial)
  const waiting = active && tone === 'deployed' && !pushHb && !sent
  return [
    'decision-pill',
    skipLock ? 'decision-pill--skip' : `decision-pill--${kind}`,
    active ? 'active' : '',
    waiting ? 'decision-pill--deployed' : '',
    pushHb ? 'decision-pill--push-hb' : '',
    sent ? 'decision-pill--sent' : '',
    partial ? 'decision-pill--partial' : '',
  ].filter(Boolean).join(' ')
}

export function sentDestinationLabel(hummingbird = false) {
  return hummingbird ? 'Hummingbird' : 'Storman'
}

export function decisionPillTitle(kind, row, ctx = {}, {
  isLeaf = true,
  childCount = 0,
  canPushToHb = false,
  pushing = false,
  sentToTenant = false,
  pushFailed = false,
  hummingbird = false,
} = {}) {
  const { decision, tone } = decisionPillTone(row, ctx)
  if (kind === 'accept' && canPushToHb) {
    if (pushing) return 'Pushing to HB'
    return pushFailed ? 'Push to HB failed' : 'Push to HB'
  }
  if (kind === 'accept' && sentToTenant && tone === 'deployed') {
    return `Sent to ${sentDestinationLabel(hummingbird)}`
  }
  if (kind === 'skip') {
    if (decision !== 'reject') {
      return isLeaf ? 'Street lock (skip export)' : `Street lock for all ${childCount} units`
    }
    return tone === 'deployed'
      ? 'Street lock until expiry'
      : 'Ready to deploy (Street lock)'
  }
  if (kind === 'accept' && decision === 'partial') {
    return isLeaf
      ? 'Accept'
      : `Some of ${childCount} units accepted — click to accept the rest`
  }
  const base = isLeaf
    ? (kind === 'accept' ? 'Accept' : 'Reject')
    : (kind === 'accept' ? `Accept all ${childCount} units` : `Reject all ${childCount} units`)
  if (decision !== kind) return base
  if (tone === 'deployed') {
    return kind === 'accept'
      ? 'Deployed'
      : 'Deployed (rejected)'
  }
  return kind === 'accept' ? 'Ready to deploy (accepted)' : 'Ready to deploy (rejected)'
}

function decisionsMatch(a, b) {
  return normalizeDecision(a) === normalizeDecision(b)
}

function persistedDecision(decision) {
  const d = normalizeDecision(decision)
  return d === 'accept' || d === 'reject'
}

/** Map UI Final-source keys (street/model/rule/override) onto deploy choice labels. */
export function uiSourceToChoice(source) {
  if (source === 'street') return 'current'
  if (source === 'model') return 'auto'
  if (source === 'rule' || source === 'override' || source === 'current' || source === 'auto') {
    return source
  }
  return null
}

/**
 * Decision written when locking a Price Control column.
 * Street is ingested from HB/Storman, so lock = keep Street (Reject payload, skip export).
 * Model / Rule follow that day's column as Final. Override holds the entered amount.
 */
export function decisionFromPriceControlLock(sourceKey) {
  return sourceKey === 'street' ? 'reject' : 'accept'
}

/**
 * Street skip/lock on this row: the leaf itself, or every child leaf locked to Street.
 * Parents do not store expiry on their own id, so getLockedChoice(parent) is null.
 */
export function isStreetSkipLocked(row, localOverrides = {}, localFinalSource = {}) {
  if (getLockedChoice(row, localOverrides, localFinalSource) === 'current') return true
  return getPriceControlLockState(row, 'street', { localOverrides, localFinalSource }).mode === 'locked'
}

/**
 * Drop the unsaved decision and Final source that a Price Control lock wrote.
 * Writes explicit `undecided` so a saved HB Accept does not come back as Push to HB.
 */
export function revertPriceControlLockDraft(
  localDecisions = {},
  localFinalSource = {},
  ids = [],
  sourceKey,
) {
  const expectedDecision = decisionFromPriceControlLock(sourceKey)
  const nextDecisions = { ...localDecisions }
  const nextSources = { ...localFinalSource }
  let decisionsChanged = false
  let sourcesChanged = false
  for (const id of ids) {
    if (nextDecisions[id] === expectedDecision) {
      nextDecisions[id] = 'undecided'
      decisionsChanged = true
    }
    if (nextSources[id] === sourceKey) {
      delete nextSources[id]
      sourcesChanged = true
    }
  }
  return {
    localDecisions: decisionsChanged ? nextDecisions : localDecisions,
    localFinalSource: sourcesChanged ? nextSources : localFinalSource,
  }
}

/**
 * Which price column the row's Final currently matches (street/model/rule/override).
 * Same rules as the Pricing Actions blue "applied" border when the user has not
 * explicitly stored a localFinalSource.
 */
export function deriveFinalSourceFromValues(values = {}) {
  const final = values.final
  if (!(final > 0)) return 'street'
  const matches = (val) => val > 0 && Math.abs(final - val) < 0.5
  if (matches(values.override)) return 'override'
  if (matches(values.rule)) return 'rule'
  if (matches(values.model)) return 'model'
  return 'street'
}

function deriveFinalSourceFromRow(row, rateMode = 'rent') {
  return deriveFinalSourceFromValues(getRowValues(row, rateMode))
}

function rentForChoice(row, choice) {
  switch (choice) {
    case 'current': return row.streetRent
    case 'auto': return row.modelRent
    case 'rule': return row.ruleRent
    case 'override': return row.overrideRent
    default: return null
  }
}

/**
 * Choice that Deploy / ready-to-deploy must persist. Prefer the user's explicit
 * Final-source click, then an override price, then the stored choice when Final
 * still matches it, otherwise the column Final already matches (so RULE Final
 * cannot deploy as RECOMMENDED).
 */
function getEffectiveChoice(row, localOverrides = {}, localFinalSource = {}) {
  const explicit = uiSourceToChoice(localFinalSource[row.id])
  if (explicit) return explicit
  if (getOverridePrice(localOverrides[row.id]) > 0) return 'override'

  const stored = row.choice && row.choice !== 'undecided' ? row.choice : (row.dbChoice || 'auto')
  const finalRent = row.finalRent
  // Incomplete fixtures / pre-agg rows have no Final yet — keep the stored choice.
  if (!(finalRent > 0)) return stored

  const storedPrice = rentForChoice(row, stored)
  if (storedPrice > 0 && Math.abs(finalRent - storedPrice) < 0.5) {
    return stored
  }

  // Final was rewritten (e.g. RULE selected) but choice label was not — trust Final
  // only when another column's price clearly owns that Final value.
  const fromFinal = uiSourceToChoice(deriveFinalSourceFromRow(row))
  if (fromFinal) {
    const matchedPrice = rentForChoice(row, fromFinal)
    if (matchedPrice > 0 && Math.abs(finalRent - matchedPrice) < 0.5) {
      return fromFinal
    }
  }
  return stored
}

/** Expiry for strategy lock / override: local draft first, then leaf fields from gRPC.
 *  An explicit `expiry: null` on the local entry clears a stored DB lock. */
export function getEffectiveExpiry(row, localOverrides = {}) {
  const local = localOverrides[row?.id]
  if (local && typeof local === 'object' && Object.prototype.hasOwnProperty.call(local, 'expiry')) {
    return local.expiry || null
  }
  return row?.overrideExpiresOn || row?.dbOverrideExpiresOn || null
}

/**
 * Priced localOverrides entry that belongs to Override (Final source override or
 * unset). Promotions also stash a price here with Final source `rule`.
 */
function localEntryIsOverridePrice(row, localOverrides = {}, localFinalSource = {}) {
  if (!row || !(getOverridePrice(localOverrides[row.id]) > 0)) return false
  const explicit = localFinalSource[row.id]
  if (explicit && explicit !== 'override') return false
  return true
}

/**
 * Expiry that belongs to an Override (priced local draft or Override lock), not
 * Street / Model / Rule strategy locks or promotions.
 */
function getOverrideExpiryForEditLeaf(row, localOverrides = {}, localFinalSource = {}) {
  if (!row) return null
  const local = localOverrides[row.id]
  if (localEntryIsOverridePrice(row, localOverrides, localFinalSource)
    && local && typeof local === 'object'
    && Object.prototype.hasOwnProperty.call(local, 'expiry')) {
    return formatPriceControlLockExpiry(local.expiry)
  }
  if (getLockedChoice(row, localOverrides, localFinalSource) === 'override') {
    return formatPriceControlLockExpiry(getEffectiveExpiry(row, localOverrides))
  }
  // Same-day / unlocked Override still carries a price + expiry; ignore leftover
  // overrideRent on Street / Model / Rule rows.
  const stored = (row.dbChoice && row.dbChoice !== 'undecided')
    ? row.dbChoice
    : (row.choice && row.choice !== 'undecided' ? row.choice : null)
  const rowOverride = Number(row.overrideRent || row.overrideRate || row.dbOverrideRent || 0)
  if (rowOverride > 0 && (!stored || stored === 'override')) {
    return formatPriceControlLockExpiry(row.overrideExpiresOn || row.dbOverrideExpiresOn)
  }
  return null
}

/** Override edit expiry: override-only date; parents need every leaf to share one. */
export function getEffectiveOverrideExpiryForEdit(
  row,
  localOverrides = {},
  localFinalSource = {},
) {
  const own = getOverrideExpiryForEditLeaf(row, localOverrides, localFinalSource)
  if (own) return own

  const leaves = priceControlLockLeaves(row)
  if (leaves.length === 0) return null

  const expiries = leaves.map((leaf) => (
    getOverrideExpiryForEditLeaf(leaf, localOverrides, localFinalSource)
  ))
  if (expiries.some((expiry) => !expiry)) return null
  const unique = [...new Set(expiries)]
  return unique.length === 1 ? unique[0] : null
}

/** Override draft timeframe for edit (week/month/day); parents need unanimous leaves. */
export function getEffectiveOverrideTimeframeForEdit(
  row,
  localOverrides = {},
  localFinalSource = {},
) {
  if (!row) return null
  if (localEntryIsOverridePrice(row, localOverrides, localFinalSource)) {
    const tf = localOverrides[row.id]?.timeframe
    if (tf === 'day' || tf === 'week' || tf === 'month') return tf
  }
  const leaves = priceControlLockLeaves(row)
  if (leaves.length === 0) return null
  const frames = leaves.map((leaf) => {
    if (!localEntryIsOverridePrice(leaf, localOverrides, localFinalSource)) return null
    const tf = localOverrides[leaf.id]?.timeframe
    return (tf === 'day' || tf === 'week' || tf === 'month') ? tf : null
  })
  if (frames.some((tf) => !tf)) return null
  const unique = [...new Set(frames)]
  return unique.length === 1 ? unique[0] : null
}

/** Default lock expiry: one week from today (calendar YYYY-MM-DD). */
export function defaultPriceControlLockExpiryISO() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Calendar expiry for a Snooze duration. Snooze is a Street Price Strategy Lock
 * for N months (same deploy path as locking Street on Pricing Actions).
 */
export function snoozeExpiryISO(months = 1, fromDate = new Date()) {
  const n = Math.max(1, Math.min(24, Math.round(Number(months) || 1)))
  const d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
  d.setMonth(d.getMonth() + n)
  return localDateISO(d)
}

/** Calendar YYYY-MM-DD in the local timezone (not UTC). */
export function localDateISO(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Add calendar days using the local timezone. */
export function addLocalDaysISO(days, fromDate = new Date()) {
  const base = fromDate instanceof Date ? fromDate : new Date(fromDate)
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  d.setDate(d.getDate() + Number(days || 0))
  return localDateISO(d)
}

/** Merge or clear lock expiry on a localOverrides entry without dropping price. */
export function withLockExpiry(entry, expiry) {
  const base = (entry && typeof entry === 'object' && !Array.isArray(entry))
    ? { ...entry }
    : (typeof entry === 'number' ? { price: entry } : {})
  base.expiry = expiry || null
  return base
}

/** Map deploy choice labels onto UI Final-source keys (street/model/rule/override). */
export function choiceToUiSource(choice) {
  if (choice === 'current') return 'street'
  if (choice === 'auto') return 'model'
  if (choice === 'rule' || choice === 'override') return choice
  return null
}

export const PRICE_CONTROL_LOCK_LABELS = {
  current: 'Street',
  auto: 'Model',
  rule: 'Rule',
  override: 'Override',
}

/** Calendar YYYY-MM-DD from ISO / Date string without UTC day shift. */
export function formatPriceControlLockExpiry(isoDate) {
  if (!isoDate) return null
  const m = String(isoDate).match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : String(isoDate).slice(0, 10)
}

/**
 * Override locks only after today. Same-day expiry stays editable (pencil, not locked).
 * Street / Model / Rule keep any set expiry as a lock.
 */
export function isOverrideLockExpiryActive(expiry, today = localDateISO()) {
  const ymd = formatPriceControlLockExpiry(expiry)
  if (!ymd) return false
  return ymd > today
}

/** Idle Override pencil: same-day priced override only, not Street / Model / Rule expiry. */
export function isIdleSameDayOverride(row, localOverrides = {}, localFinalSource = {}, today = localDateISO()) {
  if (!row) return false
  if (getLockedChoice(row, localOverrides, localFinalSource) != null) return false
  if (!(getOverridePrice(localOverrides[row.id]) > 0)) return false
  return formatPriceControlLockExpiry(getEffectiveExpiry(row, localOverrides)) === today
}

/** Short en-AU date for the lock badge (e.g. 18 Sep), calendar-safe. */
export function formatPriceControlLockExpiryShort(isoDate) {
  const ymd = formatPriceControlLockExpiry(isoDate)
  if (!ymd) return null
  const [year, month, day] = ymd.split('-').map(Number)
  if (!year || !month || !day) return ymd
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${day} ${months[month - 1]}`
}

/**
 * Deploy choice locked by an expiry for a Price Control column, or null when unlocked.
 * Prefer the stored column choice when expiry is set. Final-column derivation can
 * mis-attribute Street vs Model when those prices match.
 */
export function getLockedChoice(row, localOverrides = {}, localFinalSource = {}) {
  if (!row) return null
  const expiry = getEffectiveExpiry(row, localOverrides)
  if (!expiry) return null

  const explicit = uiSourceToChoice(localFinalSource[row.id])
  if (explicit) {
    if (explicit === 'override' && !isOverrideLockExpiryActive(expiry)) return null
    return explicit
  }

  const stored = (row.dbChoice && row.dbChoice !== 'undecided')
    ? row.dbChoice
    : (row.choice && row.choice !== 'undecided' ? row.choice : null)

  const local = localOverrides[row.id]
  const localPricedExpiry = local
    && typeof local === 'object'
    && local.expiry
    && getOverridePrice(local) > 0
  // Priced local expiry means Override only when the persisted strategy is Override
  // (or missing). A leftover price on a Street/Model/Rule lock must not win after
  // Deploy clears localFinalSource.
  if (localPricedExpiry && (!stored || stored === 'override')) {
    return isOverrideLockExpiryActive(expiry) ? 'override' : null
  }

  if (stored) {
    if (stored === 'override' && !isOverrideLockExpiryActive(expiry)) return null
    return stored
  }

  const derived = getEffectiveChoice(row, localOverrides, localFinalSource)
  if (derived === 'override' && !isOverrideLockExpiryActive(expiry)) return null
  return derived
}

function isPriceControlLeaf(row) {
  return !!row
    && row.level === 'unitType'
    && !row.subGrouping
    && (!row.children || row.children.length === 0)
}

function priceControlLockLeaves(row) {
  if (!row) return []
  if (isPriceControlLeaf(row)) return [row]
  return collectAllLeaves(row).filter((leaf) => isPriceControlLeaf(leaf))
}

/**
 * Angular `aggregateRowChoices`: parent choice is unanimous child choice,
 * otherwise `undecided` if any child is undecided, otherwise majority of
 * Street/Model/Rule. Override only wins when every child is Override.
 */
export function aggregateParentChoice(row, ctx = {}) {
  const leaves = priceControlLockLeaves(row)
  if (leaves.length === 0) return 'undecided'

  const total = { undecided: 0, current: 0, auto: 0, rule: 0, override: 0 }
  for (const leaf of leaves) {
    const src = getLeafSelectedFinalSource(leaf, getRowValues(leaf), ctx)
    const choice = uiSourceToChoice(src) || 'undecided'
    if (Object.prototype.hasOwnProperty.call(total, choice)) total[choice] += 1
    else total.undecided += 1
  }

  const n = leaves.length
  const keys = ['undecided', 'current', 'auto', 'rule', 'override']
  for (const key of keys) {
    if (total[key] === n) return key
  }
  if (total.undecided > 0) return 'undecided'

  let maxKey = 'undecided'
  let maxCount = -1
  for (const key of ['current', 'auto', 'rule']) {
    if (total[key] > maxCount) {
      maxCount = total[key]
      maxKey = key
    }
  }
  return maxKey
}

function getLeafSelectedFinalSource(row, values = {}, ctx = {}) {
  const { localOverrides = {}, localFinalSource = {} } = ctx
  const explicit = localFinalSource[row?.id]
  if (explicit === 'street' || explicit === 'model' || explicit === 'rule' || explicit === 'override') {
    return explicit
  }

  const fromLock = choiceToUiSource(getLockedChoice(row, localOverrides, localFinalSource))
  if (fromLock) return fromLock

  const stored = (row?.choice && row.choice !== 'undecided')
    ? row.choice
    : (row?.dbChoice && row.dbChoice !== 'undecided' ? row.dbChoice : null)
  const fromStored = choiceToUiSource(stored)
  if (fromStored) return fromStored

  if (row?.choice === 'undecided' || row?.dbChoice === 'undecided') return null

  return deriveFinalSourceFromValues(values)
}

/**
 * Blue "selected" Final source for Price Control cells.
 * Leaves: explicit click, then strategy lock, then stored choice, then price match.
 * Parents: Angular child-choice roll-up (not the aggregated Final dollar match).
 */
export function getSelectedFinalSource(row, values = {}, ctx = {}) {
  if (!isPriceControlLeaf(row) && priceControlLockLeaves(row).length > 0) {
    return choiceToUiSource(aggregateParentChoice(row, ctx))
  }
  return getLeafSelectedFinalSource(row, values, ctx)
}

/**
 * Leaf vs parent roll-up for a Price Control column (Angular unitTypeExpiryState).
 * - locked: solid padlock (leaf, or every child leaf locked to this column)
 * - partial: outline padlock when only some child leaves are locked
 * - none: no lock on this column
 */
export function getPriceControlLockState(row, uiSource, ctx = {}) {
  const { localOverrides = {}, localFinalSource = {} } = ctx
  const choiceKey = uiSourceToChoice(uiSource)
  const columnLabel = PRICE_CONTROL_LOCK_LABELS[choiceKey] || uiSource
  const leaves = priceControlLockLeaves(row)
  if (leaves.length === 0) return { mode: 'none' }

  const lockedLeaves = leaves.filter((leaf) => {
    const choice = getLockedChoice(leaf, localOverrides, localFinalSource)
    return choice && choiceToUiSource(choice) === uiSource
  })
  if (lockedLeaves.length === 0) return { mode: 'none' }

  const expiries = [...new Set(
    lockedLeaves
      .map((leaf) => formatPriceControlLockExpiry(getEffectiveExpiry(leaf, localOverrides)))
      .filter(Boolean),
  )]
  const isLeafRow = leaves.length === 1 && leaves[0] === row

  if (isLeafRow || lockedLeaves.length === leaves.length) {
    return {
      mode: 'locked',
      columnLabel,
      expiries,
      expiryLabel: expiries.length === 1
        ? `Expires: ${expiries[0]}`
        : (expiries.length > 1 ? 'Expiry dates for locks on child rows vary' : null),
    }
  }

  // Parent with mixed children: some locked to this column, others not.
  return {
    mode: 'partial',
    columnLabel,
    expiries,
    expiryLabel: null,
  }
}

/**
 * True when this Price Control column should show a lock (solid or outline).
 */
export function isPriceControlLocked(row, uiSource, ctx = {}) {
  return getPriceControlLockState(row, uiSource, ctx).mode !== 'none'
}

/**
 * True when a unit-type leaf has a Price Control column lock (choice + expiry).
 */
export function isPriceControlLockedLeaf(row, ctx = {}) {
  if (!row) return false
  const isLeaf = row.level === 'unitType' && !row.subGrouping
    && (!row.children || row.children.length === 0)
  if (!isLeaf) return false
  return getLockedChoice(row, ctx.localOverrides || {}, ctx.localFinalSource || {}) != null
}

/**
 * Locked filter: keep only Price Control-locked unit-type leaves and ancestors that
 * still have at least one such leaf. Also prunes amenity/size `subGroups` so
 * grouping cannot re-surface unlocked siblings.
 */
export function filterByPriceControlLock(tree, ctx = {}) {
  if (!tree?.length) return tree || []

  function filterNodes(nodes) {
    return (nodes || [])
      .map((node) => {
        const hasChildLists = (node.children?.length > 0) || (node.subGroups?.length > 0)
        const isLeaf = node.level === 'unitType' && !node.subGrouping && !hasChildLists

        if (isLeaf || !hasChildLists) {
          return isPriceControlLockedLeaf(node, ctx) ? node : null
        }

        const filteredChildren = node.children?.length ? filterNodes(node.children) : []
        const filteredSubGroups = node.subGroups?.length ? filterNodes(node.subGroups) : undefined
        if (filteredChildren.length === 0 && !(filteredSubGroups?.length > 0)) return null

        const copy = { ...node, children: filteredChildren }
        if (filteredSubGroups) copy.subGroups = filteredSubGroups
        // Amenity / size wrappers: keep children aligned with surviving leaves under subGroups.
        if (copy.subGrouping && filteredSubGroups?.length) {
          copy.children = filteredSubGroups.flatMap((sg) => sg.children || [])
          copy.childCount = copy.children.length
        }
        aggregateChildren(copy)
        return copy
      })
      .filter(Boolean)
  }

  return filterNodes(tree)
}

/**
 * Child unit-type leaves under `row` locked to `uiSource` with an expiry other
 * than `nextExpiry`. Used before parent fan-out when some children already have
 * a different lock date.
 */
export function getMixedExpiryLockLeaves(row, uiSource, nextExpiry, ctx = {}) {
  const { localOverrides = {}, localFinalSource = {} } = ctx
  const next = formatPriceControlLockExpiry(nextExpiry)
  return priceControlLockLeaves(row).filter((leaf) => {
    const choice = getLockedChoice(leaf, localOverrides, localFinalSource)
    if (!choice || choiceToUiSource(choice) !== uiSource) return false
    const existing = formatPriceControlLockExpiry(getEffectiveExpiry(leaf, localOverrides))
    return Boolean(existing && next && existing !== next)
  })
}

/**
 * Accessible plain-text summary of the lock tooltip.
 */
export function formatPriceControlLockTooltip(row, uiSource, ctx = {}) {
  const parts = getPriceControlLockTooltipParts(row, uiSource, ctx)
  if (!parts) return ''
  return [parts.heading, parts.body, parts.note, parts.expiry].filter(Boolean).join('\n')
}

/**
 * Structured copy for the Price Control lock card tooltip.
 * User-facing heading keeps Angular's "Price Strategy Lock" label.
 * @returns {{ heading: string, body: string|null, note: string|null, expiry: string|null, columnLabel: string, mode: string } | null}
 */
export function getPriceControlLockTooltipParts(row, uiSource, ctx = {}) {
  const state = getPriceControlLockState(row, uiSource, ctx)
  if (state.mode === 'none') return null

  if (state.mode === 'partial') {
    return {
      heading: 'Price Strategy Lock',
      body: null,
      note: 'There are existing locks on children of this row.',
      expiry: null,
      columnLabel: state.columnLabel,
      mode: 'partial',
    }
  }

  return {
    heading: 'Price Strategy Lock',
    body: `Price decisions for this row are locked to ${state.columnLabel}`,
    note: null,
    expiry: state.expiryLabel,
    columnLabel: state.columnLabel,
    mode: 'locked',
  }
}

/** Deploy dirty baseline from immutable DB fields (never mutated row.choice). */
function baselineChoiceFromDb(row) {
  const dbChoice = row.dbChoice || 'current'
  const dbDecision = row.dbDecision || row.decision || 'undecided'
  // Match resolvePricing: undecided CURRENT → auto for display/Final.
  if (dbChoice === 'current' && dbDecision === 'undecided') return 'auto'
  return dbChoice
}

function leafReadyToDeploy(row, ctx = {}) {
  const { localDecisions = {}, localOverrides = {}, localFinalSource = {} } = ctx
  const dbDecision = row.dbDecision || row.decision || 'undecided'
  const effective = resolveStoredDecision(row.id, dbDecision, localDecisions)
  // Do not use row.choice: applyLocalFinalSource rewrites it to the clicked source.
  const baselineChoice = baselineChoiceFromDb(row)
  const effectiveChoice = getEffectiveChoice(row, localOverrides, localFinalSource)
  const dbOverride = row.dbOverrideRent || 0
  const localOverride = getOverridePrice(localOverrides[row.id])
  const overrideChanged = effectiveChoice === 'override' && localOverride > 0 && dbOverride > 0 && localOverride !== dbOverride
  const expiryChanged = (getEffectiveExpiry(row, localOverrides) || null) !== (row.dbOverrideExpiresOn || row.overrideExpiresOn || null)
  // Local undecided over a saved Accept is a cancel, not a new deploy (keeps the idle check).
  const decisionDirty = persistedDecision(effective) && !decisionsMatch(effective, dbDecision)
  return decisionDirty || effectiveChoice !== baselineChoice || overrideChanged || expiryChanged
}

/**
 * Production Price Review state column: already saved vs accepted locally
 * but not yet written with updatePriceChanges.
 */
export function toPriceRowState(row, ctx = {}) {
  if (!row) return 'archived'
  if (ctx.isDeploying) return 'deploying'

  const isLeaf = (!row.children || row.children.length === 0) && !row.subGrouping
  if (!isLeaf) {
    const leaves = collectDecisionLeaves(row)
    if (leaves.length === 0) return 'undecided'
    const states = leaves.map(leaf => toPriceRowState(leaf, ctx))
    if (states.every(s => s === 'deploying')) return 'deploying'
    if (states.every(s => s === 'archived')) return 'archived'
    if (states.every(s => s === 'notWhitelisted')) return 'notWhitelisted'
    if (states.some(s => s === 'failedDeploy')) return 'failedDeploy'
    if (states.every(s => s === 'deployed')) return 'deployed'
    if (states.some(s => s === 'readyToDeploy')) return 'readyToDeploy'
    return 'undecided'
  }

  const dbDecision = row.dbDecision || row.decision || 'undecided'
  if (dbDecision === 'archived') return 'archived'
  if (dbDecision === 'notWhitelisted') return 'notWhitelisted'

  const effective = resolveStoredDecision(row.id, dbDecision, ctx.localDecisions || {})
  // Dirty choice / override after Sent (or Failed) is ready to Deploy again (#97).
  if (leafReadyToDeploy(row, ctx)) return 'readyToDeploy'
  if (effective === 'sentToStorman' || dbDecision === 'sentToStorman') return 'deployed'
  if (effective === 'sentToStormanFailed' || dbDecision === 'sentToStormanFailed') return 'failedDeploy'
  if (persistedDecision(dbDecision) && decisionsMatch(effective, dbDecision)) return 'deployed'
  return 'undecided'
}

export function countReadyToDeployChoices(row, ctx = {}) {
  const leaves = collectDecisionLeaves(row).filter(leaf => toPriceRowState(leaf, ctx) === 'readyToDeploy')
  const split = { current: 0, model: 0, rule: 0, override: 0, total: leaves.length }
  for (const leaf of leaves) {
    const choice = getEffectiveChoice(leaf, ctx.localOverrides, ctx.localFinalSource)
    if (choice === 'current') split.current += 1
    else if (choice === 'rule') split.rule += 1
    else if (choice === 'override') split.override += 1
    else split.model += 1
  }
  return split
}

export const ROW_DEPLOY_STATE_TOOLTIP = {
  deploying: 'Price decisions are being deployed',
  deployed: 'Price decisions are deployed',
  failedDeploy: 'Price decisions failed to deploy',
  undecided: 'Price decisions remain undecided',
  readyToDeploy: 'Price decisions ready to deploy',
  archived: 'Price decisions archived',
  notWhitelisted: 'This row is not whitelisted for pricing actions',
}

export function tallyPriceRowStats(tree, ctx = {}) {
  let total = 0
  let accepted = 0
  let rejected = 0
  let deployed = 0
  let failedExport = 0
  let archived = 0
  let notWhitelisted = 0
  const seen = new Set()
  function walk(nodes) {
    for (const node of nodes || []) {
      if (node.level === 'unitType' && !node.subGrouping && !seen.has(node.id)) {
        seen.add(node.id)
        total += 1
        const state = toPriceRowState(node, ctx)
        const effective = resolveStoredDecision(node.id, node.dbDecision || node.decision, ctx.localDecisions || {})
        if (state === 'deployed') deployed += 1
        else if (state === 'failedDeploy') failedExport += 1
        else if (state === 'archived') archived += 1
        else if (state === 'notWhitelisted') notWhitelisted += 1
        else if (state === 'readyToDeploy' && effective === 'reject') rejected += 1
        else if (state === 'readyToDeploy' && effective === 'accept') accepted += 1
      }
      if (node.children?.length) walk(node.children)
    }
  }
  walk(tree)
  const pending = total - accepted - rejected - deployed - failedExport - archived - notWhitelisted
  return { total, accepted, rejected, deployed, failedExport, archived, notWhitelisted, pending, remaining: pending }
}

/**
 * Whether a leaf row belongs under a ProgressBar status chip.
 * Pending matches tallyPriceRowStats: undecided rows plus dirty Final/override
 * changes that are readyToDeploy but not yet Accepted/Rejected.
 */
export function matchesPriceStatusFilter(statusFilter, state, effective) {
  switch (statusFilter) {
    case 'accepted':
      return state === 'readyToDeploy' && effective === 'accept'
    case 'rejected':
      return state === 'readyToDeploy' && effective === 'reject'
    case 'pending':
      if (state === 'deployed' || state === 'failedDeploy' || state === 'archived' || state === 'notWhitelisted') {
        return false
      }
      if (state === 'readyToDeploy' && (effective === 'accept' || effective === 'reject')) {
        return false
      }
      return true
    case 'deployed':
      return state === 'deployed'
    case 'failedExport':
      return state === 'failedDeploy'
    case 'archived':
      return state === 'archived'
    case 'notWhitelisted':
      return state === 'notWhitelisted'
    default:
      return true
  }
}

/**
 * Ids to write when accept/reject is clicked on a node: the node itself plus
 * every descendant leaf (state → location → group → unit type).
 */
export function getDecisionCascadeIds(tree, nodeId) {
  const node = resolveNodeFromTree(tree, nodeId)
  if (!node) return [nodeId]
  const isLeaf = (!node.children || node.children.length === 0) && !node.subGrouping
  if (isLeaf) return [nodeId]

  const ids = [nodeId]
  const seen = new Set(ids)

  function addId(id) {
    if (!seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }

  function walk(n) {
    const kids = [...(n.children || []), ...(n.subGroups || [])]
    if (kids.length === 0) {
      addId(n.id)
      return
    }
    for (const child of kids) {
      const childIsLeaf = (!child.children || child.children.length === 0) && !child.subGrouping
      if (childIsLeaf) {
        addId(child.id)
      } else {
        addId(child.id)
        walk(child)
      }
    }
  }

  walk(node)
  return ids
}

/** Skip already-saved deployed rows so they cannot be unselected until DB status changes.
 * Accept leaves Sent / approved / failed / reject / promotion / archived alone, but still
 * overwrites undecided and snooze. */
export function applyDecisionSkippingDeployed(tree, prevLocal, ids, decision, ctx = {}) {
  const next = { ...prevLocal }
  const stateCtx = { ...ctx, localDecisions: prevLocal }
  for (const id of ids) {
    const node = resolveNodeFromTree(tree, id)
    if (!node) {
      next[id] = decision
      continue
    }
    const isLeaf = (!node.children || node.children.length === 0) && !node.subGrouping

    // Street strategy lock lives on the leaf. Parent skip/Accept must not clear
    // those Reject drafts while the padlock is still on Street.
    if (getLockedChoice(node, ctx.localOverrides || {}, ctx.localFinalSource || {}) === 'current') {
      continue
    }

    if (decision === 'accept') {
      if (!isLeaf) continue
      const rowState = toPriceRowState(node, stateCtx)
      if (
        rowState === 'deployed'
        || rowState === 'failedDeploy'
        || rowState === 'archived'
        || rowState === 'notWhitelisted'
      ) continue
      const current = resolveStoredDecision(
        node.id,
        node.dbDecision || node.decision,
        prevLocal,
      )
      if (current === 'reject' || current === 'accept') continue
      next[id] = decision
      continue
    }

    // Clean Deployed stays locked. Dirty after Sent / approved is readyToDeploy (#97)
    // but must not clear Accept (Storman would drop the row from Deploy).
    if (toPriceRowState(node, stateCtx) === 'deployed') continue
    const dbDecision = node.dbDecision || node.decision || 'undecided'
    if (
      dbDecision === 'sentToStorman'
      || dbDecision === 'sentToStormanFailed'
      || dbDecision === 'approved'
      || dbDecision === 'rejected'
      || dbDecision === 'promotion'
    ) continue
    next[id] = decision
  }
  return next
}

function weightedAvg(leaves, valueFn, weightFn, ignoreZeroes = false) {
  let totalWeight = 0
  let weightedSum = 0
  for (const leaf of leaves) {
    const v = valueFn(leaf) || 0
    const w = weightFn(leaf) || 0
    if (ignoreZeroes && v <= 0) continue
    totalWeight += w
    weightedSum += v * w
  }
  const result = weightedSum / totalWeight
  return isNaN(result) ? 0 : result
}

export function aggregateChildren(node) {
  const ch = node.children
  if (ch.length === 0) return

  const leaves = collectAllLeaves(node)
  const sum = (fn) => leaves.reduce((s, c) => s + (fn(c) || 0), 0)

  node.rentableUnits = sum(c => c.rentableUnits)
  node.occupiedUnits = sum(c => c.occupiedUnits)
  node.vacantUnits = sum(c => c.vacantUnits)
  node.reservedUnits = sum(c => c.reservedUnits)
  node.totalUnits = sum(c => c.totalUnits)
  node.repairUnits = sum(c => c.repairUnits)
  node.companyUnits = sum(c => c.companyUnits)
  node.moveInUnits = sum(c => c.moveInUnits)
  node.moveOutUnits = sum(c => c.moveOutUnits)
  node.rentedUnits = sum(c => c.rentedUnits)
  node.rented28dUnits = sum(c => c.rented28dUnits)
  node.sqmRentable = sum(c => c.sqmRentable)
  node.sqmRented = sum(c => c.sqmRented)
  node.sqmOccupied = sum(c => c.sqmOccupied)
  node.sqmTotal = sum(c => c.sqmTotal)
  node.sqmVacant = sum(c => c.sqmVacant)
  node.sqmReserved = sum(c => c.sqmReserved)
  node.sqmCompany = sum(c => c.sqmCompany)
  node.sqmRepair = sum(c => c.sqmRepair)

  node.streetRent = weightedAvg(leaves, c => c.streetRent, c => c.totalUnits)
  node.streetRate = weightedAvg(leaves, c => c.streetRate, c => c.sqmTotal)
  node.modelRent = weightedAvg(leaves, c => c.modelRent, c => c.totalUnits)
  node.modelRate = weightedAvg(leaves, c => c.modelRate, c => c.sqmTotal)
  node.ruleRent = weightedAvg(leaves, c => c.ruleRent, c => c.totalUnits)
  node.ruleRate = weightedAvg(leaves, c => c.ruleRate, c => c.sqmTotal)
  node.overrideRent = weightedAvg(leaves, c => c.overrideRent || c.streetRent, c => c.totalUnits)
  node.overrideRate = weightedAvg(leaves, c => c.overrideRate || c.streetRate, c => c.sqmTotal)
  node.finalRent = weightedAvg(leaves, c => c.finalRent || c.streetRent, c => c.totalUnits)
  node.finalRate = weightedAvg(leaves, c => c.finalRate || c.streetRate, c => c.sqmTotal)
  node.moveInRent = weightedAvg(leaves, c => c.moveInRent, c => c.rentedUnits + c.vacantUnits, true)
  node.moveInRate = weightedAvg(leaves, c => c.moveInRate, c => c.sqmRented + c.sqmVacant, true)
  node.moveOutRent = weightedAvg(leaves, c => c.moveOutRent, c => c.vacantUnits, true)
  node.moveOutRate = weightedAvg(leaves, c => c.moveOutRate, c => c.sqmVacant, true)
  node.occupiedRent = weightedAvg(leaves, c => c.occupiedRent, c => c.rentedUnits, true)
  node.occupiedRate = weightedAvg(leaves, c => c.occupiedRate, c => c.sqmRented, true)
  node.competitorRent = weightedAvg(leaves, c => c.competitorRent, c => c.rentableUnits, true)
  node.competitorRate = weightedAvg(leaves, c => c.competitorRate, c => c.sqmRentable, true)

  node.changePct = pctChange(node.streetRent, node.finalRent)
  node.changePctRate = pctChange(node.streetRate, node.finalRate)

  node.occupancy = node.rentableUnits > 0 ? (node.occupiedUnits / node.rentableUnits) * 100 : 0
  const marketDenom = node.rentableUnits - node.repairUnits - node.companyUnits
  node.marketOccupancy = marketDenom > 0
    ? ((node.rentedUnits + node.reservedUnits) / marketDenom) * 100
    : node.occupancy

  node.occUnits7d = sum(c => c.occUnits7d)
  node.occUnits28d = sum(c => c.occUnits28d)
  node.occUnits1y = sum(c => c.occUnits1y)
  node.rentUnits7d = sum(c => c.rentUnits7d)
  node.rentUnits28d = sum(c => c.rentUnits28d)
  node.rentUnits1y = sum(c => c.rentUnits1y)
  const pastOcc7d = node.rentUnits7d > 0 ? (node.occUnits7d / node.rentUnits7d) * 100 : null
  const pastOcc28d = node.rentUnits28d > 0 ? (node.occUnits28d / node.rentUnits28d) * 100 : null
  const pastOcc1y = node.rentUnits1y > 0 ? (node.occUnits1y / node.rentUnits1y) * 100 : null
  node.occDelta7d = pastOcc7d != null ? node.occupancy - pastOcc7d : null
  node.occDelta28d = pastOcc28d != null ? node.occupancy - pastOcc28d : null
  node.occDelta1y = pastOcc1y != null ? node.occupancy - pastOcc1y : null

  node.occSqm7d = sum(c => c.occSqm7d)
  node.occSqm28d = sum(c => c.occSqm28d)
  node.occSqm1y = sum(c => c.occSqm1y)
  node.rentSqm7d = sum(c => c.rentSqm7d)
  node.rentSqm28d = sum(c => c.rentSqm28d)
  node.rentSqm1y = sum(c => c.rentSqm1y)
  node.occupancySqm = node.sqmRentable > 0 ? (node.sqmOccupied / node.sqmRentable) * 100 : 0
  const pastOccSqm7d = node.rentSqm7d > 0 ? (node.occSqm7d / node.rentSqm7d) * 100 : null
  const pastOccSqm28d = node.rentSqm28d > 0 ? (node.occSqm28d / node.rentSqm28d) * 100 : null
  const pastOccSqm1y = node.rentSqm1y > 0 ? (node.occSqm1y / node.rentSqm1y) * 100 : null
  node.occDeltaSqm7d = pastOccSqm7d != null ? node.occupancySqm - pastOccSqm7d : null
  node.occDeltaSqm28d = pastOccSqm28d != null ? node.occupancySqm - pastOccSqm28d : null
  node.occDeltaSqm1y = pastOccSqm1y != null ? node.occupancySqm - pastOccSqm1y : null

  node.monthlyRevenue = sum(c => c.monthlyRevenue)

  node.childCount = ch.reduce((s, c) => s + (c.childCount || 1), 0)
  recordPricingNode('rollup', node)
}

// Override entries may be stored as a bare number (legacy) or as
// { price, expiry?, timeframe?, isRate? } (current). This helper extracts the price.
export function getOverridePrice(entry) {
  if (entry == null) return 0
  if (typeof entry === 'number') return entry
  if (typeof entry === 'object' && entry.price != null) return Number(entry.price) || 0
  return 0
}

/** Round override prices the same way as chart drag: rates keep 2dp, rents are whole dollars. */
export function roundOverridePrice(value, isRate) {
  if (value == null || Number.isNaN(Number(value))) return 0
  const n = Number(value)
  return isRate ? Math.round(n * 100) / 100 : Math.round(n)
}

function overrideEntryIsRate(entry, rateMode) {
  if (entry && typeof entry === 'object' && typeof entry.isRate === 'boolean') {
    return entry.isRate
  }
  return rateMode === 'rate'
}

/** Occupancy-change deltas for the active Unit / m² toggle. */
export function getOccDeltas(row, rateMode = 'rent') {
  if (rateMode === 'rate') {
    return {
      occDelta7d: row.occDeltaSqm7d,
      occDelta28d: row.occDeltaSqm28d,
      occDelta1y: row.occDeltaSqm1y,
    }
  }
  return {
    occDelta7d: row.occDelta7d,
    occDelta28d: row.occDelta28d,
    occDelta1y: row.occDelta1y,
  }
}

/**
 * Name-cell / Visual Analytics stock segments for Unit vs m² mode.
 * m² uses aggregated sqm fields; reserved/company/repair fall back to area×units.
 */
export function getNameBarStock(row, rateMode = 'rent') {
  if (rateMode === 'rate') {
    const occupied = row.sqmOccupied ?? 0
    const reserved = row.sqmReserved ?? 0
    const vacant = row.sqmVacant ?? 0
    const company = row.sqmCompany ?? 0
    const repair = row.sqmRepair ?? 0
    const total = row.sqmTotal || row.sqmRentable
      || (occupied + reserved + vacant + company + repair)
      || 0
    return { occupied, reserved, vacant, company, repair, total, unit: 'm²' }
  }
  const occupied = row.occupiedUnits ?? 0
  const reserved = row.reservedUnits ?? 0
  const vacant = row.vacantUnits ?? 0
  const company = row.companyUnits ?? 0
  const repair = row.repairUnits ?? 0
  const total = row.totalUnits || row.rentableUnits
    || (occupied + reserved + vacant + company + repair)
    || 0
  return { occupied, reserved, vacant, company, repair, total, unit: 'units' }
}

/** Physical occupancy % for the active Unit / m² toggle. */
export function getDisplayOccupancy(row, rateMode = 'rent', { market = false } = {}) {
  if (market) return row.marketOccupancy ?? 0
  if (rateMode === 'rate') return row.occupancySqm ?? 0
  return row.occupancy ?? 0
}

/** Hover text for override cells: duration / expiry when known. */
export function formatOverrideHoverTitle(
  row,
  localOverrides = {},
  priceLabel = '',
  localFinalSource = {},
) {
  const parts = priceLabel ? [priceLabel] : []
  const timeframe = getEffectiveOverrideTimeframeForEdit(row, localOverrides, localFinalSource)
  if (timeframe) parts.push(`Duration: ${timeframe}`)
  const expiry = getEffectiveOverrideExpiryForEdit(row, localOverrides, localFinalSource)
  if (expiry) parts.push(`Expires: ${expiry}`)
  return parts.join(' · ')
}

// KSS unit-group display order (matches Angular FilterSortService.unitGroupIdRelativeValue).
export function unitGroupRelativeValue(id) {
  switch (id) {
    case 'LCK': return 0
    case 'S-3': return 1
    case 'S-7': return 2
    case 'S-11': return 3
    case 'S-15': return 4
    case 'S-22': return 5
    case 'S-48': return 6
    case 'S-ES': return 7
    case 'WIN-1': return 8
    case 'WIN-2': return 9
    case 'WIN-3': return 10
    case 'PRK': return 11
    case 'GUN': return 12
    case 'DB': return 13
    case 'UNKNOWN': return 14
    default: return 999
  }
}

// KSS unit-type order within a site (matches Angular FilterSortService.unitTypeRelativeValue).
export function unitTypeRelativeValue(row) {
  const parts = String(row.path || row.id || '').split(KEY_DELIMITER)
  const ug = parts.length >= 4 ? parts[3] : ''
  const area = Number(row.size) || 0
  switch (ug) {
    case 'LCK':
      return 1 + area * 0.001
    case 'WIN-1':
      return 20000 + area * 0.1
    case 'WIN-2':
      return 30000 + area * 0.1
    case 'WIN-3':
      return 40000 + area * 0.1
    case 'PRK':
      return 50000 + area * 0.1
    case 'GUN':
      return 60000 + area * 0.1
    case 'DB':
      return 70000 + area * 0.1
    case 'UNKNOWN':
      return 80000 + area * 0.1
    default:
      return 10000 + area * 0.1
  }
}

function compareNameRows(a, b, sortDir) {
  const mult = sortDir === 'asc' ? 1 : -1
  if (a.level === 'unitGroup' && b.level === 'unitGroup') {
    return (unitGroupRelativeValue(a.name) - unitGroupRelativeValue(b.name)) * mult
  }
  if (
    a.level === 'unitType' && b.level === 'unitType'
    && (!a.subGrouping || a.subGrouping === 'none')
    && (!b.subGrouping || b.subGrouping === 'none')
  ) {
    return (unitTypeRelativeValue(a) - unitTypeRelativeValue(b)) * mult
  }
  return String(a.name || '').localeCompare(String(b.name || '')) * mult
}

// Per-row "applied source" selection. Lets the user pick which price source
// (street/model/rule/override) is the final price for a row WITHOUT touching
// the override price column. Runs AFTER applyLocalOverrides so it can replace
// the final value chosen there with a different source's value.
export function applyLocalFinalSource(tree, finalSources, rateMode) {
  if (!finalSources || Object.keys(finalSources).length === 0) return

  const r = rateMode === 'rate'
  function pick(node, src) {
    switch (src) {
      case 'street': return r ? node.streetRate : node.streetRent
      case 'model': return r ? node.modelRate : node.modelRent
      case 'rule': return r ? node.ruleRate : node.ruleRent
      case 'override': return r ? node.overrideRate : node.overrideRent
      default: return null
    }
  }

  function applyToNode(node) {
    if (!node.children || node.children.length === 0) {
      const src = finalSources[node.id]
      if (!src) return
      const val = pick(node, src)
      if (val == null || val <= 0) return
      // Keep choice in sync with the Final source so Deploy persists RULE/STREET/
      // MODEL/OVERRIDE even if localFinalSource is missing from the call context.
      const choice = uiSourceToChoice(src)
      if (choice) node.choice = choice
      // Only replace finalRent/finalRate; never touch overrideRent/overrideRate.
      if (r) {
        node.finalRate = val
        node.changePctRate = pctChange(node.streetRate, val)
        if (node.streetRate > 0 && node.streetRent > 0) {
          const ratio = val / node.streetRate
          const rentEquivalent = node.streetRent * ratio
          node.finalRent = rentEquivalent
          node.changePct = pctChange(node.streetRent, rentEquivalent)
        }
      } else {
        node.finalRent = val
        node.changePct = pctChange(node.streetRent, val)
        if (node.streetRent > 0 && node.streetRate > 0) {
          const ratio = val / node.streetRent
          const rateEquivalent = node.streetRate * ratio
          node.finalRate = rateEquivalent
          node.changePctRate = pctChange(node.streetRate, rateEquivalent)
        }
      }
    } else {
      for (const child of node.children) applyToNode(child)
      // Re-aggregate so the parent's finalRent/finalRate reflects updated leaves.
      aggregateChildren(node)
    }
  }

  for (const node of tree) applyToNode(node)
}

// Apply locally-stored prices (kss-price-overrides) to the tree. A price whose
// decision is 'promotion' is a rule-based action, so it is written to the RULE
// column (ruleRent/ruleRate); every other price is a manual override and is
// written to the OVERRIDE column (overrideRent/overrideRate). In both cases the
// final price is updated to match. `decisions` is the kss-price-decisions map.
export function applyLocalOverrides(tree, overrides, rateMode, decisions) {
  if (!overrides || Object.keys(overrides).length === 0) return

  function applyToNode(node) {
    if (!node.children || node.children.length === 0) {
      const entry = overrides[node.id]
      const ov = getOverridePrice(entry)
      if (ov != null && ov > 0) {
        const isPromotion = decisions?.[node.id] === 'promotion'
        const asRate = overrideEntryIsRate(entry, rateMode)
        // Write both rate ($/m²/wk) and rent ($/unit/wk) so toggling view preserves the price.
        // Prefer the entry's stored isRate so Unit/m² toggles do not reinterpret the locked value.
        if (asRate) {
          if (isPromotion) node.ruleRate = ov
          else node.overrideRate = ov
          node.finalRate = ov
          node.changePctRate = pctChange(node.streetRate, ov)
          if (node.streetRate > 0 && node.streetRent > 0) {
            const ratio = ov / node.streetRate
            const rentEquivalent = node.streetRent * ratio
            if (isPromotion) node.ruleRent = rentEquivalent
            else node.overrideRent = rentEquivalent
            node.finalRent = rentEquivalent
            node.changePct = pctChange(node.streetRent, rentEquivalent)
          }
        } else {
          if (isPromotion) node.ruleRent = ov
          else node.overrideRent = ov
          node.finalRent = ov
          node.changePct = pctChange(node.streetRent, ov)
          if (node.streetRent > 0 && node.streetRate > 0) {
            const ratio = ov / node.streetRent
            const rateEquivalent = node.streetRate * ratio
            if (isPromotion) node.ruleRate = rateEquivalent
            else node.overrideRate = rateEquivalent
            node.finalRate = rateEquivalent
            node.changePctRate = pctChange(node.streetRate, rateEquivalent)
          }
        }
      }
    } else {
      for (const child of node.children) applyToNode(child)
      aggregateChildren(node)
    }
  }

  for (const root of tree) applyToNode(root)
}

export function buildTree(unitTypePaths, aggregatedData, siteNames, priceChanges, unitTypeAreas) {
  if (!unitTypePaths?.unitTypePathsList) return []

  const stateMap = new Map()

  for (const path of unitTypePaths.unitTypePathsList) {
    const parts = parsePath(path)
    if (parts.length < 3) continue

    const stateKey = getStateKey(parts)
    const locationKey = getLocationKey(parts)
    const unitGroupKey = getUnitGroupKey(parts)

    if (!stateMap.has(stateKey)) {
      stateMap.set(stateKey, {
        id: stateKey, path: stateKey, name: getStateName(parts),
        level: 'state', children: [], childCount: 0,
        expanded: false, detailsOpen: false,
        ...emptyAgg(),
        _locationMap: new Map(),
      })
    }
    const stateNode = stateMap.get(stateKey)

    if (!stateNode._locationMap.has(locationKey)) {
      const locName = siteNames.get(parts[2]) || siteNames.get(locationKey) || parts[2] || locationKey
      const locNode = {
        id: locationKey, path: locationKey, name: locName,
        level: 'location', children: [], childCount: 0,
        expanded: false, detailsOpen: false,
        ...emptyAgg(),
        _unitGroupMap: new Map(),
      }
      stateNode._locationMap.set(locationKey, locNode)
      stateNode.children.push(locNode)
    }
    const locNode = stateNode._locationMap.get(locationKey)

    const leaf = buildLeafRow(path, parts, aggregatedData, siteNames, priceChanges, unitTypeAreas)

    if (unitGroupKey && parts.length >= 5) {
      if (!locNode._unitGroupMap.has(unitGroupKey)) {
        const ugNode = {
          id: unitGroupKey, path: unitGroupKey, name: parts[3],
          level: 'unitGroup', children: [], childCount: 0,
          expanded: false, detailsOpen: false,
          ...emptyAgg(),
        }
        locNode._unitGroupMap.set(unitGroupKey, ugNode)
        locNode.children.push(ugNode)
      }
      const ugNode = locNode._unitGroupMap.get(unitGroupKey)
      ugNode.children.push(leaf)
    } else {
      locNode.children.push(leaf)
    }
  }

  for (const stateNode of stateMap.values()) {
    for (const locNode of stateNode.children) {
      for (const child of locNode.children) {
        if (child.level === 'unitGroup') {
          child.children.sort((a, b) => unitTypeRelativeValue(a) - unitTypeRelativeValue(b))
          aggregateChildren(child)
        }
      }
      locNode.children.sort((a, b) => {
        if (a.level === 'unitGroup' && b.level === 'unitGroup') {
          return unitGroupRelativeValue(a.name) - unitGroupRelativeValue(b.name)
        }
        return a.name.localeCompare(b.name)
      })
      aggregateChildren(locNode)
      delete locNode._unitGroupMap
    }
    aggregateChildren(stateNode)
    delete stateNode._locationMap
    stateNode.children.sort((a, b) => a.name.localeCompare(b.name))
  }

  return Array.from(stateMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

// === Sub-Grouping (Unit Type & Amenity) ===

export function getAmenitySuffix(name) {
  const parts = name.split('-')
  for (const suf of AMENITY_SUFFIXES) {
    if (parts.includes(suf)) return suf
  }
  return undefined
}

/** Range labels for occupancy filter plots; used for display and URL params. */
export const AREA_BAND_RANGES = [
  { label: '0-1', min: 0, max: 1 },
  { label: '1-3.5', min: 1, max: 3.5 },
  { label: '3.5-7', min: 3.5, max: 7 },
  { label: '7-11', min: 7, max: 11 },
  { label: '11-15', min: 11, max: 15 },
  { label: '15-22', min: 15, max: 22 },
  { label: '22-32', min: 22, max: 32 },
  { label: '32-48', min: 32, max: 48 },
  { label: '48-68', min: 48, max: 68 },
  { label: '68-100', min: 68, max: 100 },
  { label: '100+', min: 100, max: Infinity },
]

export function areaToRangeLabel(area) {
  if (area == null || Number.isNaN(area)) return null
  for (const r of AREA_BAND_RANGES) {
    if (area >= r.min && area < r.max) return r.label
  }
  return AREA_BAND_RANGES[AREA_BAND_RANGES.length - 1].label
}

export function toAreaBand(area, isLck) {
  const band = (units) => (Math.ceil(area / units) * units).toFixed(2)
  if (area < 1 || isLck) return band(0.1)
  if (area < 3.5) return band(0.25)
  if (area < 7) return band(0.5)
  if (area < 11) return band(1)
  if (area < 15) return band(1.5)
  if (area < 22) return band(2)
  if (area < 36) return band(3)
  if (area < 48) return band(4)
  if (area < 69) return band(6)
  if (area < 100) return band(8)
  return band(10)
}

function lookupArea(leafNode, unitTypeAreas) {
  if (!unitTypeAreas || unitTypeAreas.size === 0) return 0
  const parts = (leafNode.id || leafNode.path || '').split(KEY_DELIMITER)
  const utId = parts.length >= 5 ? parts[4] : parts.length === 4 ? parts[3] : null
  if (utId) return unitTypeAreas.get(utId) || 0
  return 0
}

function addSubGroupsToUnitGroup(ugNode, unitTypeAreas) {
  const nameParts = ugNode.name.split('-')
  if (nameParts.some(p => IGNORED_UNIT_GROUPS.includes(p))) return
  const isLck = nameParts.includes('LCK')

  const utGroupMap = new Map()

  for (const ut of ugNode.children || []) {
    const suffix = getAmenitySuffix(ut.name || ut.id)
    if (!suffix) continue

    const area = lookupArea(ut, unitTypeAreas)
    if (area <= 0) continue

    const band = toAreaBand(area, isLck)
    const key = `${band}-${suffix}`
    if (!utGroupMap.has(key)) utGroupMap.set(key, [])
    utGroupMap.get(key).push(ut)
  }

  if (utGroupMap.size === 0) return

  const amenityMap = new Map()

  for (const [name, unitTypes] of utGroupMap) {
    const utGroupRow = {
      id: `${ugNode.id}${KEY_DELIMITER}utg${KEY_DELIMITER}${name}`,
      path: ugNode.path,
      name,
      level: 'unitType',
      subGrouping: 'unit-type-group',
      children: unitTypes,
      subGroups: [],
      childCount: unitTypes.length,
      expanded: false,
      detailsOpen: false,
      ...emptyAgg(),
      flags: null,
    }
    aggregateChildren(utGroupRow)

    const suffix = name.split('-').pop()
    if (!amenityMap.has(suffix)) amenityMap.set(suffix, [])
    amenityMap.get(suffix).push(utGroupRow)
  }

  const amenityRows = []
  for (const [suffix, utGroupRows] of amenityMap) {
    utGroupRows.sort((a, b) => {
      const na = parseFloat(a.name)
      const nb = parseFloat(b.name)
      if (!isNaN(na) && !isNaN(nb)) return na - nb
      return a.name.localeCompare(b.name)
    })

    const allLeaves = utGroupRows.flatMap(g => g.children)
    const amenityRow = {
      id: `${ugNode.id}${KEY_DELIMITER}amg${KEY_DELIMITER}${suffix}`,
      path: ugNode.path,
      name: suffix,
      level: 'unitType',
      subGrouping: 'unit-amenities-group',
      children: allLeaves,
      subGroups: utGroupRows,
      childCount: allLeaves.length,
      expanded: false,
      detailsOpen: false,
      ...emptyAgg(),
      flags: null,
    }
    aggregateChildren(amenityRow)
    amenityRows.push(amenityRow)
  }

  const suffixOrder = (s) => {
    const idx = AMENITY_SUFFIXES.indexOf(s)
    return idx >= 0 ? idx : 999
  }
  amenityRows.sort((a, b) => suffixOrder(a.name) - suffixOrder(b.name))

  ugNode.subGroups = amenityRows
}

export function buildSubGroups(tree, unitTypeAreas) {
  function walkNodes(nodes) {
    for (const node of nodes) {
      if (node.level === 'unitGroup') {
        addSubGroupsToUnitGroup(node, unitTypeAreas)
      } else if (node.children?.length > 0) {
        walkNodes(node.children)
      }
    }
  }
  walkNodes(tree)
}

export function getDisplayChildren(node, groupingState) {
  const showAmenities = groupingState?.amenities === 'grouped'
  const showUnitType = groupingState?.unitType === 'grouped'

  if (node.subGroups?.length > 0 && (showAmenities || showUnitType)) {
    if (node.level === 'unitGroup') {
      if (showAmenities) return node.subGroups
      if (showUnitType) return node.subGroups.flatMap(sg => sg.subGroups || [])
    }
    if (node.subGrouping === 'unit-amenities-group' && showUnitType) {
      return node.subGroups
    }
  }

  return node.children || []
}

function checkBoundPct(baseVal, finalVal, bounds) {
  if (!baseVal || baseVal <= 0 || !finalVal || finalVal <= 0) return { enabled: false }
  const pct = ((finalVal - baseVal) / baseVal) * 100
  if (pct > Math.abs(bounds.upper)) return { enabled: true, bound: 'above' }
  if (pct < -Math.abs(bounds.lower)) return { enabled: true, bound: 'below' }
  return { enabled: false }
}

function checkGroupOutlier(node, finalVal, bounds, useRate = false) {
  if (!node._siblings || node._siblings.length < 2) return { enabled: false }
  const vals = node._siblings.map(s => (useRate ? s.finalRate : s.finalRent) || 0).filter(v => v > 0)
  if (vals.length < 2) return { enabled: false }
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length
  const variance = vals.reduce((a, v) => a + (v - mean) ** 2, 0) / vals.length
  const stdDev = Math.sqrt(variance)
  if (stdDev === 0) return { enabled: false }
  const zScore = (finalVal - mean) / stdDev
  if (zScore > Math.abs(bounds.upper)) return { enabled: true, bound: 'above' }
  if (zScore < -Math.abs(bounds.lower)) return { enabled: true, bound: 'below' }
  return { enabled: false }
}

export function computeFlags(tree, flagBounds, rateMode) {
  if (!flagBounds) return

  const useRate = rateMode === 'rate'

  function walkSetSiblings(nodes) {
    for (const node of nodes) {
      if (node.children?.length > 0) {
        const leaves = node.children.filter(c => c.level === 'unitType')
        for (const leaf of leaves) leaf._siblings = leaves
        walkSetSiblings(node.children)
      }
    }
  }
  walkSetSiblings(tree)

  function walkComputeFlags(nodes) {
    for (const node of nodes) {
      if (node.children?.length > 0) walkComputeFlags(node.children)

      const finalPrice = (useRate ? node.finalRate : node.finalRent) || 0
      if (finalPrice <= 0) { node.flags = null; continue }

      const street = (useRate ? node.streetRate : node.streetRent) || 0
      const occupied = (useRate ? node.occupiedRate : node.occupiedRent) || 0
      const competitor = (useRate ? node.competitorRate : node.competitorRent) || 0

      const competitorFlag = competitor > 0
        ? checkBoundPct(competitor, finalPrice, flagBounds.competitorDiff)
        : { enabled: false }
      if (competitorFlag.enabled && competitor > 0) {
        competitorFlag.pct = ((finalPrice - competitor) / competitor) * 100
        competitorFlag.competitorVal = competitor
        competitorFlag.finalVal = finalPrice
      }

      const flags = {
        marketRent: checkBoundPct(street, finalPrice, flagBounds.marketRent),
        actualRent: occupied > 0
          ? checkBoundPct(occupied, finalPrice, flagBounds.actualRent)
          : { enabled: false },
        competitorDiff: competitorFlag,
        groupOutlier: node.level === 'unitType'
          ? checkGroupOutlier(node, finalPrice, flagBounds.groupOutlier, useRate)
          : { enabled: false },
      }

      const hasAny = flags.marketRent.enabled || flags.actualRent.enabled ||
        flags.competitorDiff.enabled || flags.groupOutlier.enabled
      node.flags = hasAny ? flags : null

      if (!hasAny && node.children?.length > 0) {
        const childHasFlags = node.children.some(c => c.flags != null)
        if (childHasFlags) {
          node.flags = {
            marketRent: node.children.some(c => c.flags?.marketRent?.enabled)
              ? { enabled: true, bound: 'child' } : { enabled: false },
            actualRent: node.children.some(c => c.flags?.actualRent?.enabled)
              ? { enabled: true, bound: 'child' } : { enabled: false },
            competitorDiff: node.children.some(c => c.flags?.competitorDiff?.enabled)
              ? { enabled: true, bound: 'child' } : { enabled: false },
            groupOutlier: node.children.some(c => c.flags?.groupOutlier?.enabled)
              ? { enabled: true, bound: 'child' } : { enabled: false },
          }
        }
      }
    }
  }
  walkComputeFlags(tree)
}

export function collectLeaves(tree) {
  const leaves = []
  function walk(nodes) {
    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        leaves.push(node)
      } else {
        walk(node.children)
      }
    }
  }
  walk(tree)
  return leaves
}

export function flattenForDisplay(rows, expandedSet, groupingState) {
  const result = []
  function walk(nodes, depth) {
    for (const node of nodes) {
      result.push({ ...node, depth })
      if (expandedSet.has(node.id)) {
        const childrenToShow = getDisplayChildren(node, groupingState)
        if (childrenToShow?.length > 0) walk(childrenToShow, depth + 1)
      }
    }
  }
  walk(rows, 0)
  return result
}

export function applyFilters(rows, filters) {
  if (!filters) return rows

  const storeGroupStores = filters.storeGroup?.stores?.length > 0 ? new Set(filters.storeGroup.stores) : null

  function matches(row) {
    if (filters.states?.length > 0) {
      const stateName = row.path.split(KEY_DELIMITER)[1]
      if (!filters.states.includes(stateName) && !filters.states.includes(row.name)) return false
    }
    if (filters.locations?.length > 0) {
      const parts = row.path.split(KEY_DELIMITER)
      const locKey = parts.slice(0, 3).join(KEY_DELIMITER)
      if (!filters.locations.some(l => row.path.startsWith(l) || locKey === l)) return false
    }
    if (storeGroupStores) {
      const parts = row.path.split(KEY_DELIMITER)
      const locKey = parts.slice(0, 3).join(KEY_DELIMITER)
      if (!storeGroupStores.has(locKey)) return false
    }
    if (filters.unitGroups?.length > 0) {
      const parts = row.path.split(KEY_DELIMITER)
      if (parts.length >= 4 && !filters.unitGroups.includes(parts[3])) return false
    }
    if (filters.unitTypes?.length > 0) {
      const parts = row.path.split(KEY_DELIMITER)
      const utName = parts.length > 4 ? parts.slice(4).join(' / ') : parts.length === 4 ? parts[3] : parts[parts.length - 1]
      if (!filters.unitTypes.includes(utName)) return false
    }
    if (filters.areaBands?.length > 0) {
      const rangeLabel = areaToRangeLabel(row.size)
      if (!rangeLabel || !filters.areaBands.includes(rangeLabel)) return false
    }
    if (filters.whitelistOnly && filters.whitelist?.length > 0) {
      const parts = row.path.split(KEY_DELIMITER)
      const locKey = parts.slice(0, 3).join(KEY_DELIMITER)
      if (!filters.whitelist.some(w => locKey.includes(w))) return false
    }
    return true
  }

  function filterTree(nodes) {
    return nodes
      .map(node => {
        if (node.children?.length > 0) {
          const filteredChildren = filterTree(node.children)
          if (filteredChildren.length === 0) return null
          const filtered = { ...node, children: filteredChildren }
          aggregateChildren(filtered)
          return filtered
        }
        return matches(node) ? node : null
      })
      .filter(Boolean)
  }

  return filterTree(rows)
}

/** Filter tree by WTF categories; removes location nodes whose site WTF is not in allowed set. */
export function filterByWtf(tree, wtfFilterSet, wtfMap) {
  if (!wtfFilterSet?.size || !wtfMap) return tree

  function filterNodes(nodes) {
    return nodes
      .map(node => {
        if (node.level === 'location') {
          const parts = node.id.split(KEY_DELIMITER)
          const siteId = parts[2]
          const wtf = wtfMap.get(siteId) ?? 3
          if (!wtfFilterSet.has(wtf)) return null
        }
        if (node.children?.length > 0) {
          const filtered = filterNodes(node.children)
          if (filtered.length === 0) return null
          return { ...node, children: filtered }
        }
        return node
      })
      .filter(Boolean)
  }

  const filtered = filterNodes(tree)
  for (const stateNode of filtered) {
    if (stateNode.children?.length) aggregateChildren(stateNode)
  }
  return filtered
}

/** True when the row has an active Override price (not promotion / Street/Model/Rule leftover). */
function rowHasActiveOverridePrice(row, localOverrides = {}, localFinalSource = {}) {
  if (!row) return false
  if (localEntryIsOverridePrice(row, localOverrides, localFinalSource)) return true
  const locked = getLockedChoice(row, localOverrides, localFinalSource)
  if (locked && locked !== 'override') return false
  const explicit = localFinalSource[row.id]
  if (explicit && explicit !== 'override') return false
  const stored = (row.dbChoice && row.dbChoice !== 'undecided')
    ? row.dbChoice
    : (row.choice && row.choice !== 'undecided' ? row.choice : null)
  if (stored && stored !== 'override') return false
  return Number(row.overrideRent || row.overrideRate || row.dbOverrideRent || 0) > 0
}

function pricingAdvancedFieldValue(row, field, opts = {}) {
  const {
    isRate = false,
    localDecisions = {},
    localOverrides = {},
    localFinalSource = {},
  } = opts
  switch (field) {
    case 'occupancy':
      return Number(isRate ? (row.occupancySqm ?? row.occupancy) : row.occupancy) || 0
    case 'marketOccupancy':
      return Number(row.marketOccupancy) || 0
    case 'changePct':
      return Number(isRate ? (row.changePctRate ?? row.changePct) : row.changePct) || 0
    case 'streetPrice':
      return Number(isRate ? row.streetRate : row.streetRent) || 0
    case 'modelPrice':
      return Number(isRate ? row.modelRate : row.modelRent) || 0
    case 'rulePrice':
      return Number(isRate ? row.ruleRate : row.ruleRent) || 0
    case 'finalPrice':
      return Number(isRate ? row.finalRate : row.finalRent) || 0
    case 'moveInPrice':
      return Number(isRate ? row.moveInRate : row.moveInRent) || 0
    case 'unitType':
      return row.name || row.unitTypeName || ''
    case 'amenity':
      return row.amenity || ''
    case 'unitArea':
      return Number(row.size) || 0
    case 'decision': {
      // Override confirm stores decision=accept + a priced localOverrides entry.
      // Classify active overrides as 'override' so Decision=Override matches.
      if (rowHasActiveOverridePrice(row, localOverrides, localFinalSource)) return 'override'
      const d = resolveStoredDecision(row.id, row.dbDecision || row.decision, localDecisions)
      if (d === 'accept') return 'accept'
      if (d === 'reject' || d === 'snooze' || d === 'rejected') return 'snooze'
      if (d === 'override') return 'override'
      return 'undecided'
    }
    case 'hasOverride':
      return rowHasActiveOverridePrice(row, localOverrides, localFinalSource)
    case 'hasExpiry':
      return Boolean(getEffectiveExpiry(row, localOverrides))
    default:
      return row[field]
  }
}

function rowMatchesPricingAdvancedFilter(row, filter, opts = {}) {
  if (!filter || !isAdvancedFilterEnabled(filter)) return true
  const type = filter.type || 'number'
  const raw = pricingAdvancedFieldValue(row, filter.field, opts)
  if (type === 'number') {
    const bound = buildNumberBoundFromOperator(filter.operator, filter.value, filter.value2)
    if (!bound) return true
    return matchesNumberBound(Number(raw), bound)
  }
  if (type === 'string') return matchesStringFilter(raw, filter.operator, filter.value)
  if (type === 'choice') return String(raw ?? '') === String(filter.value ?? '')
  if (type === 'boolean') return matchesBooleanFilter(raw, filter.value)
  return true
}

/** Keep only leaves whose ids are in `allowedIds` (and prune empty parents). */
function pruneTreeToLeafIds(nodes, allowedIds) {
  if (!nodes?.length || !allowedIds?.size) return []
  return nodes
    .map((node) => {
      const hasChildren = node.children?.length > 0
      const hasSubGroups = node.subGroups?.length > 0
      if (!hasChildren && !hasSubGroups) {
        return allowedIds.has(node.id) ? node : null
      }
      const children = pruneTreeToLeafIds(node.children || [], allowedIds)
      const subGroups = pruneTreeToLeafIds(node.subGroups || [], allowedIds)
      if (children.length === 0 && subGroups.length === 0) return null
      const copy = { ...node, children }
      if (node.subGroups) copy.subGroups = subGroups
      if (copy.subGrouping && subGroups.length) {
        copy.children = subGroups.flatMap((sg) => sg.children || [])
        copy.childCount = copy.children.length
      }
      return copy
    })
    .filter(Boolean)
}

/** Keep tree nodes whose leaves match every enabled advanced filter. */
export function filterTreeByAdvancedFilters(tree, advancedFilters, opts = {}) {
  if (!tree?.length) return tree || []
  const active = (advancedFilters || []).filter(isAdvancedFilterEnabled)
  if (!active.length) return tree

  const leafMatches = (node) => active.every((f) => rowMatchesPricingAdvancedFilter(node, f, opts))

  function filterNodes(nodes) {
    if (!nodes?.length) return []
    return nodes
      .map((node) => {
        const hasChildren = node.children?.length > 0
        const hasSubGroups = node.subGroups?.length > 0
        if (hasChildren || hasSubGroups) {
          // Filter children first; they are the source of truth after sidebar filters.
          const children = hasChildren ? filterNodes(node.children) : []
          // Do not keep a parent solely via subGroups when children were pruned away.
          if (hasChildren && children.length === 0) return null

          let subGroups = []
          if (hasSubGroups) {
            let source = node.subGroups
            if (hasChildren) {
              const allowedIds = new Set()
              for (const child of children) {
                for (const leaf of collectAllLeaves(child)) allowedIds.add(leaf.id)
              }
              source = pruneTreeToLeafIds(node.subGroups, allowedIds)
            }
            subGroups = filterNodes(source)
          }

          if (children.length === 0 && subGroups.length === 0) return null
          const copy = { ...node, children }
          if (hasSubGroups) copy.subGroups = subGroups
          if (copy.subGrouping && subGroups.length) {
            copy.children = subGroups.flatMap((sg) => sg.children || [])
            copy.childCount = copy.children.length
          }
          aggregateChildren(copy)
          return copy
        }
        return leafMatches(node) ? node : null
      })
      .filter(Boolean)
  }

  return filterNodes(tree)
}

/** Get parent context string for a node when shown in flat/single-level view. */
export function getRowContext(node, siteNames) {
  const path = node.path || node.id || ''
  const parts = path.split(KEY_DELIMITER)
  if (parts.length < 2) return null
  const level = node.level || (node.children?.length ? null : 'unitType')
  const stateName = parts[1]
  if (level === 'state') return null
  if (level === 'location') return stateName
  if (parts.length < 3) return null
  const siteId = parts[2]
  const locKey = parts.slice(0, 3).join(KEY_DELIMITER)
  const siteName = (siteNames instanceof Map ? siteNames.get(siteId) : siteNames?.[siteId]) || (siteNames instanceof Map ? siteNames.get(locKey) : siteNames?.[locKey]) || siteId
  if (level === 'unitGroup') return `${stateName} › ${siteName}`
  if (level === 'unitType') {
    const ugName = parts.length >= 4 ? parts[3] : null
    return ugName ? `${stateName} › ${siteName} › ${ugName}` : `${stateName} › ${siteName}`
  }
  return `${stateName} › ${siteName}`
}

function resolveSiteDisplayName(pathParts, siteNames) {
  if (!pathParts?.[2]) return null
  const siteId = pathParts[2]
  const locKey = pathParts.slice(0, 3).join(KEY_DELIMITER)
  return (siteNames instanceof Map ? siteNames.get(siteId) : siteNames?.[siteId])
    || (siteNames instanceof Map ? siteNames.get(locKey) : siteNames?.[locKey])
    || siteId
}

/** Modal label: bare name for unique rows; Site > Group > … for groups and bands. */
export function formatPricingModalRowLabel(row, siteNames) {
  if (!row) return ''
  const name = row.name || ''
  const isLeafUnitType = row.level === 'unitType'
    && !row.subGrouping
    && !(row.children?.length > 0)
  if (row.level === 'state' || row.level === 'location' || isLeafUnitType) {
    return name
  }

  const path = row.path || row.id || ''
  const parts = String(path).split(KEY_DELIMITER).filter(Boolean)
  const siteName = resolveSiteDisplayName(parts, siteNames)
  const ugName = parts[3] || null
  const segments = []
  if (siteName) segments.push(siteName)

  if (row.level === 'unitGroup') {
    if (name) segments.push(name)
    return segments.join(' > ') || name
  }

  if (row.subGrouping === 'unit-amenities-group') {
    if (ugName) segments.push(ugName)
    if (name) segments.push(name)
    return segments.join(' > ') || name
  }

  if (row.subGrouping === 'unit-type-group') {
    if (ugName) segments.push(ugName)
    const amenity = getAmenitySuffix(name)
    if (amenity && amenity !== name) segments.push(amenity)
    if (name) segments.push(name)
    return segments.join(' > ') || name
  }

  if (ugName && ugName !== name) segments.push(ugName)
  if (name) segments.push(name)
  return segments.join(' > ') || name
}

/** Extract all nodes at a given level into a flat array. */
export function extractNodesAtLevel(tree, level) {
  const isSubGroupLevel = level === 'amenityGroup' || level === 'unitTypeGroup'
  const targetSubGrouping = level === 'amenityGroup' ? 'unit-amenities-group'
    : level === 'unitTypeGroup' ? 'unit-type-group' : null

  const result = []
  function walk(nodes) {
    for (const node of nodes) {
      if (isSubGroupLevel) {
        if (node.subGrouping === targetSubGrouping) result.push(node)
        if (node.subGroups?.length) walk(node.subGroups)
      } else if (!node.subGrouping) {
        const nodeLevel = node.level || (node.children?.length ? null : 'unitType')
        if (nodeLevel === level) result.push(node)
      }
      if (node.children?.length) walk(node.children)
    }
  }
  walk(tree)
  return result
}

// Keys for which a missing/null value should always sink to the end (regardless of sortDir),
// so "biggest losers/winners" sorts don't include rows that simply have no historical data.
const NULL_SINK_SORT_KEYS = new Set(['occDelta7d', 'occDelta28d', 'occDelta1y', 'wtf'])

export function sortRows(rows, sortKey, sortDir, rateMode = 'rent', options = {}) {
  if (!sortKey || !sortDir) return rows

  const r = rateMode === 'rate'
  const { wtfMap } = options
  const getVal = (row) => {
    switch (sortKey) {
      case 'name': return row.name
      case 'occupancy': return getDisplayOccupancy(row, rateMode)
      case 'marketOccupancy': return row.marketOccupancy
      case 'street': return r ? row.streetRate : row.streetRent
      case 'model': return r ? row.modelRate : row.modelRent
      case 'rule': return r ? row.ruleRate : row.ruleRent
      case 'override': return r ? row.overrideRate : row.overrideRent
      case 'final': return r ? row.finalRate : row.finalRent
      case 'changePct': return row.changePct
      case 'moveIn': return r ? row.moveInRate : row.moveInRent
      case 'occupied': return r ? row.occupiedRate : row.occupiedRent
      case 'competitor': return r ? row.competitorRate : row.competitorRent
      case 'size': return row.size || 0
      case 'amenity': return row.amenity || ''
      case 'occDelta7d': return r ? row.occDeltaSqm7d : row.occDelta7d
      case 'occDelta28d': return r ? row.occDeltaSqm28d : row.occDelta28d
      case 'occDelta1y': return r ? row.occDeltaSqm1y : row.occDelta1y
      case 'wtf': {
        if (!wtfMap) return null
        const siteId = (row.path || row.id || '').split(KEY_DELIMITER)[2]
        if (!siteId) return null
        const v = wtfMap.get(siteId)
        return v == null ? null : v
      }
      default: return 0
    }
  }

  const nullsLast = NULL_SINK_SORT_KEYS.has(sortKey)

  const sorted = [...rows].sort((a, b) => {
    if (sortKey === 'name') return compareNameRows(a, b, sortDir)
    const va = getVal(a)
    const vb = getVal(b)
    if (nullsLast) {
      const aMissing = va == null || Number.isNaN(va)
      const bMissing = vb == null || Number.isNaN(vb)
      if (aMissing && bMissing) return 0
      if (aMissing) return 1
      if (bMissing) return -1
    }
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va)
    return sortDir === 'asc' ? va - vb : vb - va
  })

  return sorted.map(row => ({
    ...row,
    children: row.children ? sortRows(row.children, sortKey, sortDir, rateMode, options) : [],
    subGroups: row.subGroups?.length > 0 ? sortRows(row.subGroups, sortKey, sortDir, rateMode, options) : row.subGroups,
  }))
}

export function extractFilterOptions(tree, siteNames, activeFilters) {
  const states = new Set()
  const locations = new Map()
  const unitGroups = new Set()
  const unitTypes = new Set()

  const selStates = activeFilters?.states?.length > 0 ? new Set(activeFilters.states) : null
  const selLocations = activeFilters?.locations?.length > 0 ? new Set(activeFilters.locations) : null
  const selUnitGroups = activeFilters?.unitGroups?.length > 0 ? new Set(activeFilters.unitGroups) : null

  for (const stateNode of tree) {
    states.add(stateNode.name)
    if (selStates && !selStates.has(stateNode.name)) continue

    for (const locNode of (stateNode.children || [])) {
      locations.set(locNode.id, locNode.name)
      if (selLocations && !selLocations.has(locNode.id)) continue

      for (const child of (locNode.children || [])) {
        if (child.level === 'unitGroup') {
          unitGroups.add(child.name)
          if (selUnitGroups && !selUnitGroups.has(child.name)) continue
          for (const leaf of (child.children || [])) {
            unitTypes.add(leaf.name)
          }
        } else {
          unitTypes.add(child.name)
        }
      }
    }
  }

  return {
    states: [...states].sort(),
    locations: [...locations.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    unitGroups: [...unitGroups].sort(),
    unitTypes: [...unitTypes].sort(),
  }
}

export function getRowValues(row, rateMode) {
  const r = rateMode === 'rate'
  return {
    street: r ? row.streetRate : row.streetRent,
    model: r ? row.modelRate : row.modelRent,
    rule: r ? row.ruleRate : row.ruleRent,
    override: r ? row.overrideRate : row.overrideRent,
    final: r ? row.finalRate : row.finalRent,
    changePct: row.changePct,
    moveIn: r ? row.moveInRate : row.moveInRent,
    occupied: r ? row.occupiedRate : row.occupiedRent,
    competitor: r ? row.competitorRate : row.competitorRent,
  }
}

/** Collect all nodes visible in the pricing-line tree view (based on expanded state). */
export function collectVisibleTreeNodes(nodes, expandedSet, groupingState) {
  const result = []
  function walk(nodelist) {
    for (const node of nodelist) {
      result.push(node)
      if (expandedSet.has(node.id)) {
        const childrenToShow = getDisplayChildren(node, groupingState)
        if (childrenToShow?.length) walk(childrenToShow)
      }
    }
  }
  walk(nodes)
  return result
}

/** Compute shared axis min/max from an array of nodes so sibling rows use the same scale. */
export function getAxisBounds(nodes, rateMode) {
  // For $/m²/wk values typical magnitudes are well below the $50 snap that makes sense for $/wk;
  // use a finer snap so rate-mode plots don't all collapse to [0,50].
  const SNAP = rateMode === 'rate' ? 1 : 50
  const vals = []
  for (const node of nodes || []) {
    const v = getRowValues(node, rateMode)
    for (const key of ['street', 'model', 'rule', 'override', 'final', 'moveIn', 'occupied', 'competitor']) {
      const n = v[key]
      if (n != null && n > 0) vals.push(n)
    }
  }
  if (!vals.length) return { axisMin: 0, axisMax: rateMode === 'rate' ? 10 : 100 }
  const rawMin = Math.min(...vals)
  const rawMax = Math.max(...vals)
  const axisMin = Math.floor(rawMin / SNAP) * SNAP
  const axisMax = Math.ceil(rawMax / SNAP) * SNAP
  return { axisMin, axisMax: Math.max(axisMax, axisMin + SNAP) }
}

export function exportToCsv(data, rateMode) {
  const levelRows = { state: [], location: [], unitGroup: [], unitType: [] }
  const header = 'Level - State,Level - Location,Level - Unit Group,Level - Unit Type,Name,Street,Model,Rule,Override,Final,%Chg,Move In,Occupied,Competitor,Occupancy,Decision'

  function csvField(val) {
    const s = String(val ?? '')
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
  }

  function walk(nodes, ctx) {
    for (const node of nodes) {
      const cur = { ...ctx }
      if (node.level === 'state') cur.state = node.name
      else if (node.level === 'location') cur.location = node.name
      else if (node.level === 'unitGroup') cur.unitGroup = node.name
      else if (node.level === 'unitType') cur.unitType = node.name

      const v = getRowValues(node, rateMode)
      levelRows[node.level]?.push(
        [
          csvField(cur.state), csvField(cur.location),
          csvField(cur.unitGroup), csvField(cur.unitType),
          csvField(node.name),
          Math.round(v.street), Math.round(v.model), Math.round(v.rule),
          Math.round(v.override), Math.round(v.final), v.changePct.toFixed(1),
          Math.round(v.moveIn), Math.round(v.occupied), Math.round(v.competitor),
          node.occupancy.toFixed(1), csvField(node.decision),
        ].join(',')
      )
      if (node.children) walk(node.children, cur)
    }
  }
  walk(data, { state: '', location: '', unitGroup: '', unitType: '' })

  const date = new Date().toISOString().split('T')[0]
  const levelFileNames = {
    state: `pricing-actions-state-${date}.csv`,
    location: `pricing-actions-location-${date}.csv`,
    unitGroup: `pricing-actions-unit-group-${date}.csv`,
    unitType: `pricing-actions-unit-type-${date}.csv`,
  }

  for (const level of LEVELS) {
    if (levelRows[level].length === 0) continue
    const csv = header + '\n' + levelRows[level].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = levelFileNames[level]
    a.click()
    URL.revokeObjectURL(url)
  }
}

/**
 * Build NewPriceChange payloads for updatePriceChanges.
 * Connect expects numeric PriceChangeSource / PriceChangeStatus enums and
 * unitTypeId (last path segment), not the UI tree path / string choice labels.
 */
export function buildDeployPayload(data, localDecisions, ctx = {}) {
  const changes = []
  const seen = new Set()
  const fullCtx = { localDecisions, ...ctx }
  function walk(nodes) {
    for (const node of nodes || []) {
      if (node.level === 'unitType' && !node.subGrouping && !seen.has(node.id)) {
        seen.add(node.id)
        if (toPriceRowState(node, fullCtx) === 'readyToDeploy') {
          const decision = resolveStoredDecision(node.id, node.decision, localDecisions)
          const path = node.path || node.id || ''
          const unitTypeId = path.split(KEY_DELIMITER).pop()
          if (unitTypeId && decision === 'accept') {
            const choiceKey = getEffectiveChoice(node, fullCtx.localOverrides, fullCtx.localFinalSource)
            const choice = choiceToPriceChangeSource(choiceKey)
            const change = {
              unitTypeId,
              manualPrice: choiceKey === 'override' && node.overrideRent > 0 ? node.overrideRent : -1,
              choice,
              status: 1, // PriceChangeStatus.APPROVED
            }
            // Angular sends choiceExpires for any strategy (Street/Model/Rule/Override).
            const expiry = getEffectiveExpiry(node, fullCtx.localOverrides)
            if (expiry) change.dateOverrideExpires = expiry
            changes.push(change)
          } else if (unitTypeId && decision === 'reject') {
            const change = {
              unitTypeId,
              manualPrice: -1,
              choice: choiceToPriceChangeSource('current'),
              status: 2, // PriceChangeStatus.REJECTED
            }
            // Reject always persists CURRENT. Only keep expiry when the lock is
            // already Street; otherwise omitting clears override_expires_on so a
            // Model/Rule/Override padlock does not move onto Street.
            const expiry = getEffectiveExpiry(node, fullCtx.localOverrides)
            const lockedChoice = getLockedChoice(
              node,
              fullCtx.localOverrides,
              fullCtx.localFinalSource,
            )
            if (expiry && lockedChoice === 'current') {
              change.dateOverrideExpires = expiry
            }
            changes.push(change)
          }
        }
      }
      if (node.children?.length) walk(node.children)
    }
  }
  walk(data)
  return changes
}

/** Count accepted vs rejected rows in a deploy payload (by PriceChangeStatus). */
export function summarizeDeployPayload(payload = []) {
  let accepted = 0
  let rejected = 0
  for (const change of payload) {
    if (change.status === 1) accepted += 1
    else if (change.status === 2) rejected += 1
  }
  return { accepted, rejected, total: payload.length }
}

/** Confirm-dialog copy: deploy sends both accepts and rejects. */
export function formatDeployConfirmMessage({ accepted = 0, rejected = 0 } = {}) {
  const parts = []
  if (accepted > 0) parts.push(`${accepted} accepted`)
  if (rejected > 0) parts.push(`${rejected} rejected`)
  if (parts.length === 0) {
    return 'No accepted or rejected price changes to deploy.'
  }
  return `Deploy ${parts.join(' and ')} price change${accepted + rejected === 1 ? '' : 's'}? This action cannot be undone.`
}

/** Confirm-dialog copy for the production-style per-row Ready To Deploy action. */
export function formatReadyToDeployMessage({ isLeaf = false, hummingbird = false } = {}) {
  const liveBy = hummingbird && isLeaf
    ? 'After Deploy, you can send this unit type to Hummingbird now, or wait for 10pm AEST.'
    : 'Deployed changes will be made live by 10pm AEST.'
  if (isLeaf) {
    return `The change for this unit type is ready to deploy. ${liveBy}`
  }
  return `The changes for all unit types under this category are ready to deploy. ${liveBy}`
}

/** Hover-card copy for a saved Accept waiting for nightly export. */
export function formatDeployedMessage({ isLeaf = false } = {}) {
  if (isLeaf) {
    return 'This change is saved in Streamline. It will go live by 10pm AEST.'
  }
  return 'These changes are saved in Streamline. They will go live by 10pm AEST.'
}

/** Hover-card copy after Hummingbird / Storman has the exported rate. */
export function formatSentExportMessage({ isLeaf = false, hummingbird = false } = {}) {
  const destination = sentDestinationLabel(hummingbird)
  if (isLeaf) {
    return `Street rates for this unit type are updated in ${destination}.`
  }
  return `These changes have been exported to ${destination}.`
}

/** Success-toast copy aligned with the payload actually sent. */
export function formatDeploySuccessMessage({ accepted = 0, rejected = 0, total = 0 } = {}) {
  if (total === 0) return 'Deployed 0 price changes'
  if (accepted > 0 && rejected > 0) {
    return `Deployed ${total} price changes (${accepted} accepted, ${rejected} rejected)`
  }
  if (rejected > 0) {
    return `Deployed ${rejected} rejected price change${rejected === 1 ? '' : 's'}`
  }
  return `Deployed ${accepted} accepted price change${accepted === 1 ? '' : 's'}`
}

export function isDeployCutoff() {
  const now = new Date()
  const aestOffset = 10 * 60
  const utc = now.getTime() + now.getTimezoneOffset() * 60000
  const aest = new Date(utc + aestOffset * 60000)
  return aest.getHours() >= 21 && aest.getMinutes() >= 50
}

function formatSummaryDate(isoDate) {
  if (!isoDate) return null
  const d = new Date(`${isoDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Plain-English target phrase for pricing rule summaries. */
export function describeRuleTarget({ stateName, siteName, unitGroupName, unitTypeName }) {
  if (unitTypeName) {
    if (siteName && stateName) return `${unitTypeName} at ${siteName} (${stateName})`
    if (siteName) return `${unitTypeName} at ${siteName}`
    if (stateName) return `${unitTypeName} in ${stateName}`
    return unitTypeName
  }
  if (unitGroupName) {
    if (siteName && stateName) return `all unit types in ${unitGroupName} at ${siteName} (${stateName})`
    if (siteName) return `all unit types in ${unitGroupName} at ${siteName}`
    if (stateName) return `all unit types in ${unitGroupName} in ${stateName}`
    return `all unit types in ${unitGroupName}`
  }
  if (siteName) {
    return stateName ? `all unit types at ${siteName} (${stateName})` : `all unit types at ${siteName}`
  }
  if (stateName) return `all sites in ${stateName}`
  return null
}

/** Human-readable summary of what a pricing rule will do in the modal. */
export function buildPricingRuleSummary({
  ruleType,
  stateName,
  siteName,
  unitGroupName,
  unitTypeName,
  leafCount,
  price,
  currentStreet,
  expiryDate,
}) {
  const target = describeRuleTarget({ stateName, siteName, unitGroupName, unitTypeName })
  if (!target) {
    return 'Select a state, site, unit group, or unit type to see what this rule will do.'
  }

  const scopeSuffix = leafCount > 1 && !unitTypeName
    ? ` This covers ${leafCount} unit type${leafCount !== 1 ? 's' : ''}.`
    : ''

  const untilStr = formatSummaryDate(expiryDate)
  const untilPhrase = untilStr ? ` until ${untilStr}` : ''

  if (ruleType === 'snooze') {
    return `Snooze pricing change for ${target}${untilPhrase}. The recommended change will stay hidden until then, and Deploy skips price-change export for the unit type.${scopeSuffix}`
  }

  if (ruleType === 'model') {
    return `Lock Model price for ${target}${untilPhrase}. Deploy accepts the change and exports Model for the unit type.${scopeSuffix}`
  }

  if (ruleType === 'rule') {
    return `Lock Rule price for ${target}${untilPhrase}. Deploy accepts the change and exports Rule for the unit type.${scopeSuffix}`
  }

  const numPrice = price != null && price !== '' ? parseFloat(price) : NaN
  const hasPrice = !Number.isNaN(numPrice) && numPrice > 0

  let changePhrase = ''
  if (hasPrice && currentStreet > 0) {
    const pct = ((numPrice - currentStreet) / currentStreet) * 100
    const sign = pct >= 0 ? '+' : ''
    changePhrase = ` (${sign}${pct.toFixed(1)}% vs current street ${fmtPrice(currentStreet, false)})`
  }

  if (ruleType === 'promotion') {
    if (hasPrice) {
      return `Apply a promotional street price of ${fmtPrice(numPrice, false)} for ${target}${untilPhrase}.${changePhrase} Units are marked as promoted until the rule expires.${scopeSuffix}`
    }
    return `Apply a promotional street price for ${target}${untilPhrase}. Enter a price below to see the amount and change vs street.${scopeSuffix}`
  }

  // override (default)
  if (hasPrice) {
    return `Set the street price to ${fmtPrice(numPrice, false)} for ${target}${untilPhrase}.${changePhrase} This replaces the model recommendation until the rule expires.${scopeSuffix}`
  }
  return `Set a fixed street price for ${target}${untilPhrase}. Enter a price below to see the amount and change vs street.${scopeSuffix}`
}
